import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSWR } from "../lib/useSWR";
import { useSiteContentReady, getCachedSiteContent } from "../lib/useSiteContent";
import FadeIn from "../components/FadeIn";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Coins, Gift, ListChecks, UserCircle, ArrowRight, List, CaretLeft, CaretRight,
  House, ChartLineUp, Camera, Newspaper, Heart, Eye, Medal, Question, Waves as WavesIcon,
  UsersThree, Copy, WhatsappLogo, ClockCountdown, SidebarSimple,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import ThemeToggle from "../components/ThemeToggle";
import NotificationBell from "../components/NotificationBell";
import LanguageToggle from "../components/LanguageToggle";
import { LanguageProvider, useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";
import CitySelect from "../components/CitySelect";
import CountryCitySelect from "../components/CountryCitySelect";
import DobPicker from "../components/DobPicker";
import { trackEvent } from "../components/GAScript";
import haptic from "../lib/haptics";
import { formatDate, formatDateTime } from "../lib/date";

// Self-contained editable display-name card. Reads the user's current name,
// supports inline edit + save with server-side fake-name validation.
// Renders directly above the phone panel on the patron profile page.
function NameEditor({ user, refresh }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(user?.name || ""); }, [user?.name]);
  const save = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === (user?.name || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me/name", { name: trimmed });
      toast.success("Name updated");
      setEditing(false);
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update name");
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => { setValue(user?.name || ""); setEditing(false); };
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-4 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="profile-name-panel">
      <div className="min-w-[200px]">
        <div className="text-xs font-bold uppercase tracking-wider text-neutral-400">NAME</div>
        {editing ? (
          <div className="mt-1.5 flex items-center gap-2">
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
              disabled={saving}
              maxLength={80}
              className="border border-neutral-300 rounded-xl px-3 py-1.5 text-base min-w-[260px] focus:outline-none focus:border-mango"
              data-testid="name-input"
              placeholder="Your full name (as on CNIC)"
            />
          </div>
        ) : (
          <div className="text-base font-semibold mt-1.5" data-testid="name-display">
            {user?.name || <span className="text-neutral-400 italic font-normal">Not set yet</span>}
          </div>
        )}
        {editing && (
          <div className="text-[11px] text-neutral-500 mt-1">Use your full real name. It's needed to claim rewards or raffle prizes.</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <Button type="button" onClick={save} disabled={saving} className="bg-mango hover:bg-mango/90 text-white rounded-full" data-testid="name-save">
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button type="button" onClick={cancel} variant="outline" disabled={saving} className="rounded-full" data-testid="name-cancel">Cancel</Button>
          </>
        ) : (
          <Button type="button" variant="outline" onClick={() => setEditing(true)} className="rounded-full" data-testid="name-edit-btn">
            Edit
          </Button>
        )}
      </div>
    </div>
  );
}

// Self-contained referral / promo card. Fetches its own data and handles its
// own copy/share UX so it can be embedded on the home page or the profile
// page (or hidden) depending on admin's site-content setting
// `referral_card_visibility`.
function ReferralCard() {
  const { data: referral } = useSWR("/me/referral", null);
  const { t, lang } = useLanguage();
  // Admin-settable illustration (Site Content → Patron dashboard). Falls
  // back to the plain icon circle when unset.
  const [referralImage, setReferralImage] = useState("");
  useEffect(() => {
    api.get("/site-content").then((r) => setReferralImage((r.data?.panelist_referral_image_url || "").trim())).catch(() => {});
  }, []);
  if (!referral?.enabled) return null;
  const points = referral.points_award ?? 200;
  const waText = encodeURIComponent(`Join me on oodel. — Pakistan's market research panel. Use my code ${referral.referral_code} when you sign up: ${referral.share_url}`);
  const copyCode = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referral.share_url);
        toast.success(t("ui_ur_share_link_copied"));
        return;
      }
      throw new Error("clipboard-unsupported");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = referral.share_url; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success(t("ui_ur_share_link_copied")); else toast.error(t("ui_ur_couldnt_copy"));
      } catch { toast.error(t("ui_ur_couldnt_copy")); }
    }
  };
  const isUr = lang === "ur";
  const interp = (key) => t(key).replace("{points}", String(points));
  const shortLink = referral.share_url.replace(/^https?:\/\//, "");
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8" data-testid="referral-card">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
        {/* Left — illustration + headline, mirrors the mockup's "Invite
            friends. Earn more points." block. */}
        <div className="flex items-center gap-4">
          {referralImage ? (
            <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 bg-mango/5" data-testid="referral-image">
              <img src={referralImage} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-full bg-mango/10 flex items-center justify-center shrink-0">
              <UsersThree size={26} weight="duotone" className="text-mango" />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-neutral-900 tracking-tight" dir={isUr ? "rtl" : "ltr"}>{interp("ui_ur_referral_headline")}</h2>
            <p className="text-sm text-neutral-500 mt-0.5" dir={isUr ? "rtl" : "ltr"}>{interp("ui_ur_referral_explainer")}</p>
          </div>
        </div>

        {/* Right — referral link input (inline copy icon, mockup style) + WhatsApp + live stats */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1.5" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_referral_your_code")} <span className="text-mango font-mono normal-case">{referral.referral_code}</span></div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 bg-neutral-50 border border-neutral-200 rounded-full px-4 py-2.5 text-sm font-mono text-neutral-600 truncate">{shortLink}</div>
            <button
              onClick={copyCode}
              aria-label={t("ui_ur_copy_share_link")}
              title={t("ui_ur_copy_share_link")}
              className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 text-black flex items-center justify-center transition-colors shrink-0"
              data-testid="copy-referral"
            ><Copy size={17} weight="bold" /></button>
            <a
              href={`https://wa.me/?text=${waText}`}
              target="_blank" rel="noreferrer"
              aria-label={t("ui_ur_share_on_whatsapp")}
              title={t("ui_ur_share_on_whatsapp")}
              className="w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white flex items-center justify-center transition-colors shrink-0"
              data-testid="whatsapp-share"
            ><WhatsappLogo size={18} weight="fill" /></a>
          </div>
          <div className="flex flex-wrap gap-4 mt-3" data-testid="referral-live-stats">
            <div><span className="font-bold text-neutral-800">{referral.invited_count}</span> <span className="text-xs text-neutral-400">{t("ui_ur_referral_invited")}</span></div>
            <div><span className="font-bold text-mango">{referral.credited_count}</span> <span className="text-xs text-neutral-400">{t("ui_ur_referral_credited")}</span></div>
            <div><span className="font-bold text-neutral-800">{referral.pending_count}</span> <span className="text-xs text-neutral-400">{t("ui_ur_referral_pending")}</span></div>
            <div><span className="font-bold text-mango">{referral.total_referral_points}</span> <span className="text-xs text-neutral-400">{t("ui_ur_points")}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PanelistLayout() {
  const { user, logout, loading, loggingOut } = useAuth();
  const { t, lang } = useLanguage();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  // Hydrate the CMS-driven nav flags from a localStorage cache so the
  // sidebar paints instantly on refresh. Background fetch silently updates
  // anything the admin changed. (Stale-while-revalidate pattern.)
  const CMS_CACHE_KEY = "oodel_panelist_cms_cache_v1";
  const cmsCached = (() => {
    try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null"); } catch { return null; }
  })();
  const [raffleThreshold, setRaffleThreshold] = useState(cmsCached?.raffleThreshold ?? 5000);
  const [incentiveMode, setIncentiveMode] = useState(cmsCached?.incentiveMode ?? "rewards");
  const [pointsHistoryEnabled, setPointsHistoryEnabled] = useState(cmsCached?.pointsHistoryEnabled ?? true);
  const [pointsMenuLabel, setPointsMenuLabel] = useState(cmsCached?.pointsMenuLabel ?? "Points");
  // Admin-controlled visibility of the patron public-content menus.
  const [navTrackers, setNavTrackers] = useState(cmsCached?.navTrackers ?? true);
  const [navSnapshots, setNavSnapshots] = useState(cmsCached?.navSnapshots ?? true);
  const [navArticles, setNavArticles] = useState(cmsCached?.navArticles ?? true);
  const [navSentiment, setNavSentiment] = useState(cmsCached?.navSentiment ?? true);
  const [navHelp, setNavHelp] = useState(cmsCached?.navHelp ?? true);
  // Only block the sidebar with "Loading…" on the very first ever visit (no
  // cache yet). Every subsequent refresh paints instantly.
  const [cmsLoaded, setCmsLoaded] = useState(!!cmsCached);
  // Help dialog open state — declared at the top so the hook order is stable.
  const [helpOpen, setHelpOpen] = useState(false);
  // Desktop sidebar collapse state — persisted so a returning patron keeps
  // their preference across sessions.
  const SIDEBAR_KEY = "oodel_panelist_sidebar_collapsed_v1";
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0"); } catch { /* quota */ }
  };
  useEffect(() => {
    if (loggingOut) return;
    // Accept `patron`, `panelist`, AND any legacy `growth_lead` role that
    // pre-dates the "GL = patron+badge" refactor. Otherwise we'd bounce
    // GL users between /admin and /panelist and cause a redirect loop.
    if (!loading && (!user || (user.role !== "patron" && user.role !== "panelist" && !user.is_growth_lead && user.role !== "growth_lead"))) nav("/login");
  }, [user, loading, loggingOut, nav]);
  useEffect(() => {
    api.get("/site-content").then((r) => {
      const t = parseInt(r.data?.raffle_base_threshold || "5000", 10);
      const flag = (k) => (r.data?.[k] || "true").toLowerCase() !== "false";
      const next = {
        raffleThreshold: Number.isNaN(t) ? 5000 : t,
        incentiveMode: (r.data?.incentive_mode || "rewards").toLowerCase(),
        pointsHistoryEnabled: (r.data?.points_history_enabled || "true").toLowerCase() !== "false",
        pointsMenuLabel: (r.data?.points_history_menu_label || "").trim() || "Points",
        navTrackers: flag("panelist_nav_trackers_enabled"),
        navSnapshots: flag("panelist_nav_snapshots_enabled"),
        navArticles: flag("panelist_nav_articles_enabled"),
        navSentiment: flag("panelist_nav_sentiment_enabled"),
        navHelp: flag("panelist_nav_help_enabled"),
      };
      setRaffleThreshold(next.raffleThreshold);
      setIncentiveMode(next.incentiveMode);
      setPointsHistoryEnabled(next.pointsHistoryEnabled);
      setPointsMenuLabel(next.pointsMenuLabel);
      setNavTrackers(next.navTrackers);
      setNavSnapshots(next.navSnapshots);
      setNavArticles(next.navArticles);
      setNavSentiment(next.navSentiment);
      setNavHelp(next.navHelp);
      try { localStorage.setItem(CMS_CACHE_KEY, JSON.stringify(next)); } catch { /* quota */ }
    }).catch(() => {}).finally(() => setCmsLoaded(true));
  }, []);
  const showPointsToast = () => {
    if (!user) return;
    // Use display_points so the toast matches the nav pill + points page.
    // CA / ex-CA users have peer-referral credits filtered out of this value.
    const have = (user.display_points ?? user.points) || 0;
    const need = raffleThreshold - have;
    if (need <= 0) {
      // Strong celebratory haptic — user just realised they're eligible.
      haptic.success();
      toast.success(t("ui_ur_raffle_eligible").replace("{have}", have.toLocaleString()));
    } else {
      haptic.tap();
      toast(t("ui_ur_raffle_need_more").replace("{need}", need.toLocaleString()).replace("{threshold}", raffleThreshold.toLocaleString()), { duration: 4500 });
    }
  };
  if (!user || !cmsLoaded) return <div className="p-12">{t("ui_ur_loading")}</div>;

  const NAV = [
    ["/panelist", t("ui_ur_nav_home"), "panelist-nav-home", (p) => p === "/panelist"],
    ["/panelist/waves", t("ui_ur_nav_waves"), "panelist-nav-waves", (p) => p.startsWith("/panelist/waves")],
    ...(navTrackers ? [["/panelist/trackers", t("ui_ur_nav_trackers"), "panelist-nav-trackers", (p) => p.startsWith("/panelist/trackers")]] : []),
    ...(navSnapshots ? [["/panelist/snapshots", t("ui_ur_nav_snapshots"), "panelist-nav-snapshots", (p) => p.startsWith("/panelist/snapshots")]] : []),
    ...(navArticles ? [["/panelist/articles", t("ui_ur_nav_articles"), "panelist-nav-articles", (p) => p.startsWith("/panelist/articles")]] : []),
    ...(navSentiment ? [["/panelist/sentiment", t("ui_ur_nav_sentiment"), "panelist-nav-sentiment", (p) => p.startsWith("/panelist/sentiment")]] : []),
    ...(navSentiment ? [["/panelist/watchlist", t("ui_ur_nav_watchlist"), "panelist-nav-watchlist", (p) => p.startsWith("/panelist/watchlist")]] : []),
    // Rewards menu is only visible when admin has set incentive_mode = "rewards".
    // When mode is "raffle" or "off" we hide it entirely so patrons aren't confused.
    ...(incentiveMode === "rewards"
      ? [["/panelist/rewards", t("ui_ur_nav_rewards"), "panelist-nav-rewards", (p) => p.startsWith("/panelist/rewards")]]
      : []),
    // Points history menu — admin-toggleable via site_content.points_history_enabled
    // (default ON; flip to "false" to hide menu entirely). Label is admin-editable
    // via CMS (`points_history_menu_label`); Urdu falls back to the CMS label so
    // admins can override per-language via the labels console.
    ...(pointsHistoryEnabled
      ? [["/panelist/points", lang === "ur" ? t("ui_ur_points_label") : pointsMenuLabel, "panelist-nav-points", (p) => p.startsWith("/panelist/points")]]
      : []),
    ["/panelist/profile", t("ui_ur_nav_profile"), "panelist-nav-profile", (p) => p.startsWith("/panelist/profile")],
    // Campus Ambassador OR Growth Lead menu — visible to CAs (patrons flagged
    // as ambassadors) and to Growth Leads (patrons flagged as growth leads).
    // Menu label differs by badge: "Ambassador" for CAs, "Dashboard" for GLs.
    ...((user.is_campus_ambassador || user.is_growth_lead)
      ? [["/panelist/ambassador",
          user.is_growth_lead ? t("ui_ur_nav_dashboard") : t("ui_ur_nav_ambassador"),
          "panelist-nav-ambassador",
          (p) => p.startsWith("/panelist/ambassador")]]
      : []),
    // Help is a "fake route" — clicking it opens the support-message dialog
    // (handled below) instead of navigating. We sentinel it with a custom
    // path so the renderer can detect + special-case the onClick handler.
    // Visibility is gated by `panelist_nav_help_enabled` CMS toggle.
    ...(navHelp ? [["__help__", t("ui_ur_nav_help"), "panelist-nav-help", () => false]] : []),
  ];
  const onLogout = () => { logout(); nav("/"); setDrawer(false); };
  const close = () => setDrawer(false);
  // Real menu items only — icon lookup keyed by the existing testid so we
  // never have to touch the NAV-building logic above (admin CMS toggles,
  // Urdu labels, growth-lead/CA conditionals all stay untouched).
  const NAV_ICONS = {
    "panelist-nav-home": House,
    "panelist-nav-waves": WavesIcon,
    "panelist-nav-trackers": ChartLineUp,
    "panelist-nav-snapshots": Camera,
    "panelist-nav-articles": Newspaper,
    "panelist-nav-sentiment": Heart,
    "panelist-nav-watchlist": Eye,
    "panelist-nav-rewards": Gift,
    "panelist-nav-points": Coins,
    "panelist-nav-profile": UserCircle,
    "panelist-nav-ambassador": Medal,
    "panelist-nav-help": Question,
  };

  return (
    <div className="min-h-screen flex bg-[#F4F7F6] themable-panelist" data-testid="panelist-shell">
      {/* Desktop sidebar — collapsible, real menu items only */}
      <aside
        className={`hidden lg:flex flex-col ${collapsed ? "w-[76px]" : "w-64"} shrink-0 bg-white border-r border-neutral-200 sticky top-0 h-screen transition-all duration-300 z-30`}
        data-testid="panelist-sidebar"
      >
        <div className={`h-16 flex items-center border-b border-neutral-100 ${collapsed ? "justify-center px-0" : "px-6"}`}>
          <Link to="/panelist" className="flex items-baseline gap-2 min-w-0">
            <span className="font-black tracking-tighter leading-none text-lg" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span className="text-mango">.</span></span>
            {!collapsed && <span className="overline text-neutral-400">PANEL</span>}
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map(([to, label, tid, match]) => {
            const Icon = NAV_ICONS[tid] || House;
            const active = to !== "__help__" && match(loc.pathname);
            const itemCls = `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${collapsed ? "justify-center" : ""} ${active ? "bg-mango/10 text-mango font-semibold" : "text-neutral-600 hover:bg-neutral-100 hover:text-black"}`;
            if (to === "__help__") {
              return (
                <button key={to} onClick={() => setHelpOpen(true)} className={`w-full ${itemCls}`} data-testid={tid} title={collapsed ? label : undefined}>
                  <Icon size={20} weight={active ? "fill" : "regular"} />
                  {!collapsed && <span>{label}</span>}
                </button>
              );
            }
            return (
              <Link key={to} to={to} className={itemCls} data-testid={tid} title={collapsed ? label : undefined}>
                <Icon size={20} weight={active ? "fill" : "regular"} />
                {!collapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-neutral-100 p-3 space-y-1">
          <div className={`flex items-center gap-2 py-2 ${collapsed ? "justify-center" : "px-1"}`}>
            <div className="w-9 h-9 rounded-full bg-mango/10 text-mango flex items-center justify-center font-bold text-sm shrink-0">{(user.name || "?")[0]}</div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{user.name}</div>
                <button onClick={onLogout} className="text-xs text-neutral-400 hover:text-red-600" data-testid="panelist-logout">{t("ui_ur_sign_out")}</button>
              </div>
            )}
          </div>
          <button
            onClick={toggleCollapsed}
            data-testid="panelist-sidebar-collapse-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="w-full flex items-center justify-center gap-2 text-xs text-neutral-400 hover:text-black py-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
          >
            {collapsed ? <CaretRight size={16} /> : <><CaretLeft size={16} /><span>{t("ui_ur_collapse")}</span></>}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header — compact bar + off-canvas drawer */}
        <header className="lg:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-neutral-200">
          <div className="px-4 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 min-w-0">
              {loc.pathname !== "/panelist" && (
                <button
                  type="button"
                  aria-label="Go back"
                  data-testid="panelist-mobile-back"
                  onClick={() => { haptic.tap(); if (window.history.length > 1) nav(-1); else nav("/panelist"); }}
                  className="-ml-1 w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-neutral-100 active:bg-neutral-200 transition-colors"
                >
                  <CaretLeft size={20} weight="bold" />
                </button>
              )}
              <Link to="/panelist" className="flex items-baseline gap-2 min-w-0">
                <span className="font-black tracking-tighter leading-none text-lg" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span className="text-mango">.</span></span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => showPointsToast()} className="font-mono text-xs bg-mango text-white px-2.5 py-1.5 rounded-full hover:bg-mango/90 transition-colors" data-testid="points-balance-mobile"><Coins size={12} className="inline mr-1" />{user.points}</button>
              <NotificationBell />
              <LanguageToggle variant="pill" />
              <Sheet open={drawer} onOpenChange={(v) => { if (v) haptic.tap(); setDrawer(v); }}>
                <SheetTrigger asChild>
                  <button data-testid="panelist-menu-trigger" aria-label="Open menu" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white">
                    <List size={18} weight="bold" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[88vw] max-w-sm p-0 border-0 bg-white flex flex-col" data-testid="panelist-drawer">
                  <SheetHeader className="p-5 border-b border-neutral-100 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                    <SheetTitle asChild><span className="font-black tracking-tighter text-lg" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span className="text-mango">.</span> <span className="overline ml-2 text-neutral-400">PANEL</span></span></SheetTitle>
                  </SheetHeader>
                  <div className="flex-1 overflow-y-auto">
                    <div className="p-5 border-b border-neutral-100">
                      <div className="overline text-neutral-400 mb-1" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_signed_in_as")}</div>
                      <div className="font-bold">{user.name}</div>
                      <div className="text-sm text-neutral-500">{user.email}</div>
                      <button onClick={() => showPointsToast()} className="mt-3 font-mono text-sm bg-mango text-white px-3 py-1.5 rounded-full inline-block hover:bg-mango/90"><Coins size={14} className="inline mr-1" />{user.display_points ?? user.points} {t("ui_ur_points")}</button>
                    </div>
                    <nav className="py-2">
                      {NAV.map(([to, label, tid, match]) => {
                        const Icon = NAV_ICONS[tid] || House;
                        const active = to !== "__help__" && match(loc.pathname);
                        return to === "__help__" ? (
                          <button
                            key={to}
                            onClick={() => { haptic.selection(); close(); setHelpOpen(true); }}
                            className="flex items-center gap-3 w-full text-left px-5 py-3 text-base border-l-2 border-transparent hover:bg-neutral-50"
                            data-testid={`m-${tid}`}
                          ><Icon size={18} />{label}</button>
                        ) : (
                          <Link
                            key={to} to={to}
                            onClick={() => { haptic.selection(); close(); }}
                            className={`flex items-center gap-3 px-5 py-3 text-base border-l-2 ${active ? 'border-mango bg-mango/5 font-semibold text-mango' : 'border-transparent hover:bg-neutral-50'}`}
                            data-testid={`m-${tid}`}
                          ><Icon size={18} weight={active ? "fill" : "regular"} />{label}</Link>
                        );
                      })}
                    </nav>
                  </div>
                  <div className="p-5 border-t border-neutral-100 flex-shrink-0 bg-white space-y-2">
                    <ThemeToggle className="w-full justify-center" />
                    <Button onClick={onLogout} className="w-full rounded-full bg-black hover:bg-neutral-800 text-white" data-testid="m-panelist-logout">{t("ui_ur_sign_out")}</Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        {/* Desktop top bar */}
        <header className="hidden lg:flex sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-neutral-200 h-16 items-center justify-end px-8 gap-4">
          <button onClick={() => showPointsToast()} className="font-mono text-sm bg-mango text-white px-4 py-1.5 rounded-full hover:bg-mango/90 transition-colors" data-testid="points-balance"><Coins size={14} className="inline mr-1.5" />{user.display_points ?? user.points} {t("ui_ur_points")}</button>
          <NotificationBell />
          <LanguageToggle variant="pill" />
          <ThemeToggle variant="pill" />
        </header>

        <main className="flex-1 max-w-6xl w-full mx-auto px-4 md:px-8 py-8 md:py-10"><Outlet /></main>
      </div>
      <PanelistHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} user={user} />
    </div>
  );
}

/**
 * Patron-facing "Help" dialog — sends a free-text message to support@oodel.co
 * via the backend (which queues it into email_outbox like any other email).
 * The patron's name + email are auto-attached so support can reply directly.
 */
function PanelistHelpDialog({ open, onClose, user }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialogBody, setDialogBody] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/site-content").then((r) => {
      setDialogBody((r.data?.panelist_help_dialog_body || "").trim());
    }).catch(() => {});
  }, [open]);
  if (!open) return null;
  const send = async () => {
    if (!msg.trim()) { toast.error("Please type a message first."); return; }
    setBusy(true);
    try {
      await api.post("/support/contact", { message: msg.trim() });
      toast.success("Sent. Our team will reply to your email shortly.");
      setMsg(""); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't send right now — please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="panelist-help-dialog" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1">NEED HELP?</div>
        <h2 className="text-2xl font-bold tracking-tight mb-2 text-neutral-900">Message the oodel team</h2>
        {dialogBody && (
          <p className="text-sm text-neutral-500 mb-4 whitespace-pre-line" data-testid="panelist-help-body">
            {dialogBody.replace(/\{email\}/g, user?.email || "")}
          </p>
        )}
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-neutral-300 rounded-xl text-sm focus:outline-none focus:border-mango"
          data-testid="panelist-help-message"
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose} variant="ghost" className="rounded-full" data-testid="panelist-help-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-mango hover:bg-mango/90 text-white rounded-full" data-testid="panelist-help-send">
            {busy ? "Sending…" : "Send message →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PanelistHome() {
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  const nav = useNavigate();
  // Profile Completion Gate: incomplete-profile patrons land on the
  // dashboard normally (see the platform, browse wave titles) — they're
  // only routed to /panelist/profile the moment they try to actually
  // interact with a wave (handled inline per-click below via
  // `profileComplete` checks, not a blanket redirect here).
  // Dashboard data — SWR-cached for instant first paint AND we expose a
  // `homeReady` flag so the data sections stay invisible (FadeIn) until the
  // primary fetches have completed at least once. This eliminates the
  // "stale cached data flashes then updates" feel: refresh shows the page
  // chrome instantly + a brief blank data area + a smooth fade-in of the
  // real current content.
  const wavesSWR = useSWR(`/waves?lang=${lang}`, []);
  const waves = wavesSWR.data;
  const completedSWR = useSWR("/me/completed-waves", []);
  const completed = completedSWR.data;
  // ── Tile + tab counters should reflect only NET-positive completions —
  // voided responses don't count as a "completed survey" from the patron's
  // mental model, and counting them inflates the dashboard numbers.
  const completedNet = (completed || []).filter((c) => !c.is_void);
  const referralSWR = useSWR("/me/referral", null);
  const referral = referralSWR.data;
  const homeReady = wavesSWR.loaded && completedSWR.loaded;
  const [tab, setTab] = useState("available");
  const revoteSWR = useSWR("/sentiment/me/due-revotes", { due_now: [], upcoming: [] });
  const revote = revoteSWR.data || { due_now: [], upcoming: [] };
  const notificationsSWR = useSWR("/me/notifications", []);
  const notifications = notificationsSWR.data || [];
  // ITER 69 — right-rail "latest content" cards (70/30 dashboard split).
  // Each block only renders once its own fetch has landed AND returned at
  // least one item — sections with no data yet just disappear rather than
  // showing an empty card.
  const latestSnapshotSWR = useSWR(`/public/snapshots/waves?lang=${lang}`, []);
  const latestSnapshot = (latestSnapshotSWR.data || [])[0] || null;
  const latestTrackersSWR = useSWR(`/public/trackers?lang=${lang}`, { trackers: [] });
  const latestTracker = (() => {
    const rows = latestTrackersSWR.data?.trackers || [];
    if (rows.length === 0) return null;
    return [...rows].sort((a, b) => (b.latest?.date || "").localeCompare(a.latest?.date || ""))[0];
  })();
  const latestArticlesSWR = useSWR(`/articles?lang=${lang}`, []);
  // ITER 107 — "Featured Article": admin marks ONE published article as
  // featured (Admin → Editorial → edit → "Featured" toggle); falls back to
  // the most recently published one if nothing's been marked yet, so the
  // card never goes empty on a fresh install.
  const latestArticle = (latestArticlesSWR.data || []).find((a) => a.is_featured) || (latestArticlesSWR.data || [])[0] || null;
  // CMS-editable confidentiality footer (Site Content → Patron dashboard).
  // English is the source of truth; `_ur` siblings are optional Urdu
  // translations — fall back to English whenever unset.
  const [footerP1, setFooterP1] = useState("");
  const [footerP2, setFooterP2] = useState("");
  const [footerP1Ur, setFooterP1Ur] = useState("");
  const [footerP2Ur, setFooterP2Ur] = useState("");
  // Hero banner illustration + editable headline/subtext (Site Content →
  // Patron dashboard). Falls back to nothing until the fetch lands.
  const [heroImage, setHeroImage] = useState("");
  const [heroHeadlineEn, setHeroHeadlineEn] = useState("Your voice helps\nshape Pakistan.");
  const [heroHeadlineUr, setHeroHeadlineUr] = useState("");
  const [heroSubtextEn, setHeroSubtextEn] = useState("Take part in surveys, read the latest findings, and earn rewards for your participation.");
  const [heroSubtextUr, setHeroSubtextUr] = useState("");
  // ITER 100 — global (site-wide, admin-set once) illustration images for
  // the two homepage spotlight cards. These are DELIBERATELY not tied to
  // any specific wave/article record — admin said these change rarely, so
  // they live in Site Content instead of being re-uploaded on every wave
  // or article. (Per-record cover images on Wave/Article are unaffected —
  // still used on the wave/article's own pages exactly as before.)
  const [currentWaveImage, setCurrentWaveImage] = useState("");
  const [latestArticleImage, setLatestArticleImage] = useState("");
  // Where should the referral card live? Admin-controlled in Site Content.
  // Defaults to "home" (current behaviour). Other values: "profile", "hidden".
  const [referralVisibility, setReferralVisibility] = useState("home");
  useEffect(() => {
    api.get("/site-content").then((r) => {
      setFooterP1((r.data?.panelist_footer_p1 || "").trim());
      setFooterP2((r.data?.panelist_footer_p2 || "").trim());
      setFooterP1Ur((r.data?.panelist_footer_p1_ur || "").trim());
      setFooterP2Ur((r.data?.panelist_footer_p2_ur || "").trim());
      setHeroImage((r.data?.panelist_hero_image_url || "").trim());
      if (r.data?.panelist_hero_headline) setHeroHeadlineEn(r.data.panelist_hero_headline);
      if (r.data?.panelist_hero_headline_ur) setHeroHeadlineUr(r.data.panelist_hero_headline_ur);
      if (r.data?.panelist_hero_subtext) setHeroSubtextEn(r.data.panelist_hero_subtext);
      if (r.data?.panelist_hero_subtext_ur) setHeroSubtextUr(r.data.panelist_hero_subtext_ur);
      setCurrentWaveImage((r.data?.panelist_current_wave_image_url || "").trim());
      setLatestArticleImage((r.data?.panelist_latest_article_image_url || "").trim());
      const v = String(r.data?.referral_card_visibility || "").trim().toLowerCase();
      setReferralVisibility(["home", "profile", "hidden"].includes(v) ? v : "home");
    }).catch(() => {});
  }, []);
  const isUrLang = lang === "ur";
  const heroHeadline = isUrLang && heroHeadlineUr ? heroHeadlineUr : heroHeadlineEn;
  const heroSubtext = isUrLang && heroSubtextUr ? heroSubtextUr : heroSubtextEn;
  const footerP1Effective = isUrLang && footerP1Ur ? footerP1Ur : footerP1;
  const footerP2Effective = isUrLang && footerP2Ur ? footerP2Ur : footerP2;
  const prof = user.profile || {};
  const profileComplete = !!prof.profile_complete && (prof.sectors_of_interest || []).length >= 3;

  const dismissNotification = async (nid) => {
    try {
      await api.post(`/me/notifications/${nid}/read`);
      notificationsSWR.setData((cur) => (cur || []).map((n) => n.id === nid ? { ...n, read_at: new Date().toISOString() } : n));
    } catch {}
  };
  const unreadCount = notifications.filter((n) => !n.read_at).length;
  // Spotlight the admin-picked "Featured Wave" (Admin → Waves → edit →
  // "Featured" toggle) on the hero row; falls back to the nearest available
  // wave (today's default behaviour) if nothing's been explicitly featured
  // yet, or if the featured one happens to not be in this patron's
  // available list (wrong sector/audience/etc — never show a dead card).
  const firstWave = homeReady && waves.length > 0 ? (waves.find((w) => w.is_featured) || waves[0]) : null;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="panelist-dashboard-redesigned">
      {/* HERO — greeting + tagline + admin-settable illustration. Solid
          brand-green card (not white) per explicit request — text uses
          white/light-green shades that stay legible against the green
          fill in BOTH light and dark theme (this card intentionally does
          NOT use `.bg-white`/`.text-neutral-900`, so it's unaffected by
          the app-wide dark-mode text-color overrides — same look either way). */}
      <div className="bg-mango rounded-3xl p-6 md:p-10 flex items-center gap-6 md:gap-10 overflow-hidden shadow-sm" data-testid="panelist-hero">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {user.is_campus_ambassador && <CampusAmbassadorBadge t={t} onGreen />}
            {user.is_founding_patron && <FoundingPatronBadge t={t} />}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-1 whitespace-pre-line" data-testid="panelist-hero-headline">{heroHeadline}</h1>
          <p className="text-emerald-50/90 mt-3 max-w-md text-sm md:text-base" data-testid="panelist-hero-subtext">{heroSubtext}</p>
        </div>
        {heroImage && (
          <div className="hidden sm:block w-[200px] md:w-[300px] lg:w-[340px] shrink-0">
            <img
              src={heroImage}
              alt=""
              className="w-full h-auto object-contain"
              data-testid="panelist-hero-image"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
        )}
      </div>

      {/* PROFILE INCOMPLETE — hard gate, no surveys can be taken until completed */}
      {!profileComplete && (
        <section className="bg-amber-50 border border-amber-300 rounded-2xl p-6 shadow-sm" data-testid="profile-incomplete-banner">
          <div className="flex items-start gap-4">
            <div className="text-3xl shrink-0">⚠️</div>
            <div className="flex-1">
              <div className="overline text-amber-700 mb-1">{t("ui_ur_profile_incomplete_overline")}</div>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight text-amber-900 mb-2">
                {t("ui_ur_profile_incomplete_title")}
              </h2>
              <p className="text-sm text-amber-800 mb-4">
                {t("ui_ur_profile_incomplete_body")}
              </p>
              <Link to="/panelist/profile">
                <Button data-testid="goto-profile" className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                  {t("ui_ur_complete_profile_now")}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MY NOTIFICATIONS — routine notifications now live in the global bell
          icon (top navbar). This section is reserved for RAFFLE WINS only,
          which deserve a celebratory inline takeover. */}
      {unreadCount > 0 && notifications.some((n) => !n.read_at && n.kind === "raffle_won") && (
        <section data-testid="my-notifications" aria-label="My notifications">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline text-mango">{t("ui_ur_congratulations")}</div>
              <h2 className="text-2xl font-bold tracking-tight">{t("ui_ur_raffle_win_heading")}</h2>
            </div>
          </div>
          <div className="space-y-3">
            {notifications.filter((n) => !n.read_at && n.kind === "raffle_won").map((n) => {
              const isWin = true;
              // Pull the draw name out of the body without exposing the prize. Body looks like:
              //   "Congratulations — you're the winner of the **Mar 2026** draw (drawn 2026-03-31)..."
              const drawMatch = n.body && n.body.match(/\*\*(.+?)\*\*/);
              const dateMatch = n.body && n.body.match(/\(drawn (\d{4}-\d{2}-\d{2})\)/);
              return (
                <div
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  className={isWin
                    ? "relative overflow-hidden rounded-2xl shadow-sm"
                    : "bg-white border border-neutral-200 rounded-2xl shadow-sm p-5"}
                >
                  {isWin ? (
                    <div className="bg-gradient-to-br from-mango via-emerald-500 to-emerald-700 text-white p-7">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="inline-block bg-black/20 text-white overline px-2 py-0.5 text-[10px] font-mono mb-3 rounded-full">{t("ui_ur_raffle_winner_pill")}</div>
                          <h3 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight mb-2">{t("ui_ur_raffle_win_title")}</h3>
                          {drawMatch && (
                            <div className="text-sm text-white/90 mt-1">
                              <span className="font-bold">{t("ui_ur_draw_label")}</span> {drawMatch[1]}{dateMatch && <> · <span className="font-mono text-xs uppercase tracking-wider">{t("ui_ur_drawn_on")} {dateMatch[1]}</span></>}
                            </div>
                          )}
                          <p className="mt-4 text-sm text-white/80 leading-relaxed max-w-xl">
                            {t("ui_ur_raffle_claim_notice")}
                          </p>
                        </div>
                        <div className="hidden md:flex flex-col items-end">
                          <div className="text-7xl leading-none">🏆</div>
                        </div>
                      </div>
                      <div className="mt-5 pt-4 border-t border-white/20 flex items-center justify-between">
                        <span className="text-xs font-mono uppercase tracking-wider text-white/70">{formatDateTime(n.created_at)}</span>
                        <Button
                          size="sm"
                          onClick={() => dismissNotification(n.id)}
                          className="bg-white hover:bg-neutral-100 text-emerald-800 rounded-full"
                          data-testid={`dismiss-notification-${n.id}`}
                        >
                          {t("ui_ur_got_it")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-lg">{n.title}</div>
                        <div className="text-sm text-neutral-600 mt-1">{n.body}</div>
                        <div className="text-xs text-neutral-400 mt-2 font-mono uppercase tracking-wider">{formatDateTime(n.created_at)}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => dismissNotification(n.id)} className="rounded-full">{t("ui_ur_dismiss")}</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(revote.due_now?.length > 0 || revote.upcoming?.length > 0) && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm" data-testid="revote-card">
          <div className="overline text-mango mb-2">{t("ui_ur_revote_overline")}</div>
          <h2 className="text-2xl font-bold tracking-tight">{t("ui_ur_revote_title")}</h2>
          <p className="text-sm text-neutral-500 mt-1 mb-4">{revote.due_now.length > 0 ? t("ui_ur_revote_ready").replace("{n}", String(revote.due_now.length)) : t("ui_ur_revote_upcoming")}</p>
          <div className="space-y-2">
            {revote.due_now.slice(0, 3).map((it) => (
              <Link to={`/panelist/sentiment/${it.slug}`} key={it.id} data-testid={`revote-due-${it.slug}`} className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 hover:border-mango hover:bg-mango/5 transition-colors">
                <div className="w-10 h-10 rounded-full bg-neutral-100 border border-neutral-200 overflow-hidden flex-shrink-0">{it.photo_url ? <img src={it.photo_url} className="w-full h-full object-cover" alt="" /> : <div className="text-lg font-bold flex items-center justify-center h-full text-neutral-300">{it.name[0]}</div>}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{it.name}</div>
                  <div className="text-xs text-neutral-500">{t("ui_ur_revote_rated").replace("{stars}", it.my_stars).replace("{days}", it.voted_days_ago)}</div>
                </div>
                <Button size="sm" className="rounded-full bg-mango hover:bg-mango/90">{t("ui_ur_revote_rerate")}</Button>
              </Link>
            ))}
            {revote.due_now.length === 0 && revote.upcoming.slice(0, 3).map((it) => (
              <div key={it.id} data-testid={`revote-upcoming-${it.slug}`} className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-neutral-300 text-neutral-500">
                <div className="w-10 h-10 rounded-full bg-neutral-100 border border-neutral-200 overflow-hidden flex-shrink-0">{it.photo_url ? <img src={it.photo_url} className="w-full h-full object-cover" alt="" /> : <div className="text-lg font-bold flex items-center justify-center h-full text-neutral-300">{it.name[0]}</div>}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate text-black">{it.name}</div>
                  <div className="text-xs">{t("ui_ur_revote_eligible_in").replace("{days}", it.eligible_in_days)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SPOTLIGHT — current wave + latest article, side by side. Mockup
          order: spotlight cards come BEFORE the stats row. */}
      <FadeIn ready={homeReady}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CurrentWaveCard wave={firstWave} profileComplete={profileComplete} lang={lang} t={t} image={currentWaveImage} />
        <LatestArticleCard article={latestArticle} t={t} image={latestArticleImage} />
      </div>
      </FadeIn>

      {/* STATS TILES — only real, existing metrics. No "Panel Rank". */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="panelist-stats-row">
        <StatTile icon={<ChartLineUp size={15} weight="bold" />} label={t("ui_ur_stat_surveys_completed")} value={homeReady ? completedNet.length : "—"} hint={t("ui_ur_stat_hint_keep_it_up")} testid="stat-surveys-completed" />
        <StatTile icon={<Coins size={15} weight="bold" />} label={t("ui_ur_stat_lifetime_points")} value={user.display_points ?? user.points} hint={t("ui_ur_stat_hint_view_history")} hintHref="/panelist/points" testid="stat-lifetime-points" />
        <StatTile icon={<UsersThree size={15} weight="bold" />} label={t("ui_ur_stat_referrals")} value={referral ? referral.invited_count : "—"} hint={t("ui_ur_stat_hint_invite_earn")} testid="stat-referrals" />
      </div>

      {referralVisibility === "home" && !user.is_growth_lead && <ReferralCard />}

      <FadeIn ready={homeReady}>
      {/* ITER 69 — 70/30 dashboard split: survey tabs get the wide left
          column, a right rail surfaces the single most recent snapshot /
          tracker / report so patrons discover editorial content without
          leaving the dashboard. Each rail card disappears entirely when
          its own feed has no data yet. */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-start">
      <div className="lg:col-span-7">
        <WavesTabsPanel tab={tab} setTab={setTab} waves={waves} completed={completed} completedNet={completedNet} profileComplete={profileComplete} lang={lang} t={t} user={user} ready={homeReady} />
      </div>

      <aside className="lg:col-span-3 space-y-4" data-testid="panelist-latest-rail">
        <LatestPanel
          testid="latest-snapshot"
          label={t("ui_ur_latest_snapshot")}
          viewAllHref="/panelist/snapshots"
          t={t}
          highlightTitle
          item={latestSnapshot ? {
            href: `/panelist/snapshots/wave/${latestSnapshot.wave_id}`,
            title: latestSnapshot.name,
            meta: `${latestSnapshot.visible_question_count} ${t(latestSnapshot.visible_question_count === 1 ? "ui_ur_insight" : "ui_ur_insights")}${latestSnapshot.date ? ` · ${formatDate(latestSnapshot.date)}` : ""}`,
            image: latestSnapshot.image,
            // ITER 108 — this is a server-rendered chart PNG (fixed
            // 1200×630 canvas with informational text right up to the
            // edges), not a decorative photo — cropping it via
            // object-cover was clipping the branding + right-edge
            // percentages. `isChart` switches LatestPanel to
            // object-contain so the whole chart is always visible.
            isChart: true,
          } : null}
        />
        <LatestPanel
          testid="latest-tracker"
          label={t("ui_ur_latest_tracker")}
          viewAllHref="/panelist/trackers"
          t={t}
          highlightTitle
          item={latestTracker ? {
            href: `/panelist/trackers/${latestTracker.id}`,
            title: latestTracker.name,
            meta: `${(latestTracker.category || "").toUpperCase()}${latestTracker.latest?.date ? ` · ${t("ui_ur_updated")} ${formatDate(latestTracker.latest.date)}` : ""}`,
            image: latestTracker.image,
            isChart: true,
          } : null}
        />
        <LatestPanel
          testid="latest-article"
          label={t("ui_ur_latest_report")}
          viewAllHref="/panelist/articles"
          t={t}
          item={latestArticle ? {
            href: `/panelist/articles/${latestArticle.slug || latestArticle.id}`,
            title: latestArticle.title,
            meta: `${sectorLabel(latestArticle.sector, lang).toUpperCase()}${latestArticle.created_at ? ` · ${formatDate(latestArticle.created_at)}` : ""}`,
            image: latestArticle.cover_image,
          } : null}
        />
      </aside>
      </div>

      {/* Confidentiality footer — copy is admin-editable via Site Content
          → Patron dashboard (English + optional Urdu translation). Falls
          back to hard-coded defaults so the page never renders an empty
          footer if the CMS value is wiped. The entire block hides when
          BOTH paragraphs are explicitly emptied. */}
      {(footerP1Effective || footerP2Effective) && (
      <div className="mt-10 pt-6 border-t border-neutral-200 text-[11px] leading-relaxed text-neutral-500 max-w-3xl" data-testid="panelist-confidentiality">
        {footerP1Effective && <p data-testid="panelist-footer-p1">{footerP1Effective}</p>}
        {footerP2Effective && <p className="mt-2" data-testid="panelist-footer-p2">{footerP2Effective}</p>}
      </div>
      )}
      </FadeIn>
    </div>
  );
}

function StatTile({ icon, label, value, hint, hintHref, testid }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid={testid}>
      <div className="text-sm text-neutral-500">{label}</div>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-2xl font-bold text-neutral-900 leading-none">{value}</span>
        <span className="text-mango">{icon}</span>
      </div>
      {hint && (hintHref ? (
        <Link to={hintHref} className="text-xs text-neutral-400 hover:text-mango mt-1.5 inline-block">{hint} →</Link>
      ) : (
        <div className="text-xs text-neutral-400 mt-1.5">{hint}</div>
      ))}
    </div>
  );
}

/**
 * "Current Wave" spotlight card — mirrors the mockup's featured survey
 * card: eyebrow label, title, description, icon meta row, compact pill
 * button. The "Closes in N days" pill renders ONLY when the admin has
 * actually set an end_date. `image` is a GLOBAL illustration set once via
 * Site Content → Patron Dashboard (NOT the per-wave cover_image) — this
 * card always shows the same image regardless of which wave is featured,
 * since admins said this artwork changes rarely (ITER 100).
 */
function CurrentWaveCard({ wave, profileComplete, lang, t, image }) {
  if (!wave) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-[220px]" data-testid="current-wave-card-empty">
        <ListChecks size={28} className="text-neutral-300 mb-3" />
        <div className="font-bold text-neutral-500">{t("ui_ur_no_wave_available")}</div>
        <p className="text-sm text-neutral-400 mt-1 max-w-xs">{t("ui_ur_no_wave_notice")}</p>
      </div>
    );
  }
  const daysLeft = wave.end_date ? Math.ceil((new Date(wave.end_date) - new Date()) / 86400000) : null;
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 h-full flex flex-col" data-testid="current-wave-card">
      <div className="flex items-start justify-between gap-4 flex-1">
        <div className="flex-1 min-w-0 flex flex-col h-full">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-mango mb-2">{t("ui_ur_current_wave")}</div>
            <h3 className="text-xl font-bold leading-snug text-neutral-900">{wave.name}</h3>
            <p className="text-sm text-neutral-500 mt-1.5 line-clamp-2">{wave.intro_text || t("ui_ur_current_wave_fallback_desc")}</p>
            <div className="flex items-center gap-4 flex-wrap mt-4 text-sm text-neutral-500" dir={lang === "ur" ? "rtl" : "ltr"}>
              <span className="flex items-center gap-1.5"><ListChecks size={15} /> {wave.question_ids.length} {t(wave.question_ids.length === 1 ? "ui_ur_question" : "ui_ur_questions")}</span>
              {wave.expected_minutes ? <span className="flex items-center gap-1.5"><ClockCountdown size={15} /> {wave.expected_minutes} {t("ui_ur_minutes")}</span> : null}
              {daysLeft !== null && (
                <span className="flex items-center gap-1.5 text-red-600 font-medium" data-testid="current-wave-closing-pill">
                  <ClockCountdown size={15} weight="bold" /> {daysLeft <= 0 ? t("ui_ur_closes_today") : t("ui_ur_closes_in_days").replace("{days}", daysLeft)}
                </span>
              )}
            </div>
          </div>
          <Link to={profileComplete ? `/panelist/survey/${wave.id}` : "/panelist/profile"} className="inline-block mt-auto pt-5">
            <Button data-testid="current-wave-take-survey" className={`rounded-full px-5 ${profileComplete ? 'bg-mango hover:bg-mango/90' : 'bg-neutral-400 hover:bg-neutral-500'}`}>
              {profileComplete ? <>{t("ui_ur_start")} <ArrowRight size={16} className="ml-2 rtl-flip-arrow" /></> : t("ui_ur_complete_profile_arrow")}
            </Button>
          </Link>
        </div>
        {image && (
          <div className="hidden sm:block w-32 h-32 rounded-xl overflow-hidden shrink-0 bg-emerald-50">
            <img src={image} alt="" className="w-full h-full object-cover" data-testid="current-wave-image" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "Latest Article" spotlight card — mirrors the mockup's editorial card
 * (renamed from "Latest Finding" per user feedback). `image` is a GLOBAL
 * illustration set once via Site Content → Patron Dashboard (NOT the
 * per-article cover_image) — always the same image regardless of which
 * article is featured (ITER 100).
 */
function LatestArticleCard({ article, t, image }) {
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  if (!article) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-[220px]" data-testid="latest-finding-card-empty">
        <Newspaper size={28} className="text-neutral-300 mb-3" />
        <div className="font-bold text-neutral-500">{t("ui_ur_no_articles_yet")}</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 h-full flex flex-col" data-testid="latest-finding-card">
      <div className="flex items-start justify-between gap-4 flex-1">
        <div className="flex-1 min-w-0 flex flex-col h-full">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-mango mb-2">{t("ui_ur_latest_article")}</div>
            <h3 className="text-xl font-bold leading-snug text-neutral-900 line-clamp-2">{article.title}</h3>
            <div className="text-xs text-neutral-400 mt-2 font-medium uppercase tracking-wide">{sectorLabel(article.sector, lang).toUpperCase()}{article.created_at ? ` · ${formatDate(article.created_at)}` : ""}</div>
          </div>
          <Link to={`/panelist/articles/${article.slug || article.id}`} className="inline-block mt-auto pt-5">
            <Button data-testid="latest-finding-read" className="rounded-full px-5 bg-mango hover:bg-mango/90 text-white">
              {t("ui_ur_read_editorial")} <ArrowRight size={16} className="ml-2 rtl-flip-arrow" />
            </Button>
          </Link>
        </div>
        {image && (
          <div className="hidden sm:block w-32 h-32 rounded-xl overflow-hidden shrink-0 bg-neutral-100">
            <img src={image} alt="" className="w-full h-full object-cover" data-testid="latest-finding-image" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ITER 97 — Available/Completed waves tabs, extracted out of PanelistHome
// so the exact same list UI can be reused on the dedicated /panelist/waves
// page (linked from the sidebar "Waves" nav item) without duplicating ~120
// lines of markup + void/screen-out classification logic.
function WavesTabsPanel({ tab, setTab, waves, completed, completedNet, profileComplete, lang, t, user, ready }) {
  return (
    <div>
      <div className="flex items-center gap-1 border-b border-neutral-200 mb-4">
        <button onClick={() => setTab("available")} data-testid="tab-available" className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === 'available' ? 'border-mango text-mango' : 'border-transparent text-neutral-500 hover:text-black'}`}>{t("ui_ur_available_surveys")} ({waves.length})</button>
        <button onClick={() => setTab("completed")} data-testid="tab-completed" className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === 'completed' ? 'border-mango text-mango' : 'border-transparent text-neutral-500 hover:text-black'}`}>{t("ui_ur_completed_surveys")} ({completedNet.length})</button>
      </div>
      {tab === "available" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="waves-grid">
          {waves.map((w) => (
            <div key={w.id} className="bg-white border border-neutral-200 rounded-xl shadow-sm hover:shadow-md hover:border-mango/40 hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col group" data-testid={`wave-${w.id}`}>
              {/* ITER 101/102/108 — per-wave `cover_image`, admin-uploadable
                  in the Wave Builder. Vertical/portrait crop (aspect-[4/5],
                  not the wide 16/9 used on Trackers/Snapshots) since these
                  are designed as a poster-style report cover, not a data
                  chart. Falls back to a plain icon tile so the grid stays
                  visually even when a wave has no cover set yet. Kept
                  compact (3-up grid, ~40% smaller footprint than the
                  original 2-up layout) so a screenful of waves stays sleek
                  rather than a handful of oversized tiles. */}
              <div className="aspect-[4/5] bg-emerald-50 overflow-hidden flex items-center justify-center" data-testid={`wave-image-${w.id}`}>
                {w.cover_image ? (
                  <img src={w.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("wave-img-fallback"); }} />
                ) : (
                  <ListChecks size={24} className="text-mango/40" />
                )}
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h3 className="text-sm font-bold leading-snug text-neutral-900 group-hover:text-mango transition-colors line-clamp-2">{w.name}</h3>
                <div className="text-[10px] uppercase tracking-wide font-mono text-neutral-400 mt-1" dir={lang === "ur" ? "rtl" : "ltr"}>
                  {w.question_ids.length} {t(w.question_ids.length === 1 ? "ui_ur_question" : "ui_ur_questions")}
                  {w.expected_minutes ? ` · ~${w.expected_minutes} ${t("ui_ur_minutes")}` : ""}
                </div>
                <Link to={profileComplete ? `/panelist/survey/${w.id}` : "/panelist/profile"} onClick={() => { if (!profileComplete) toast.error(t("ui_ur_complete_profile_toast")); }} className="mt-auto pt-2 border-t border-neutral-100">
                  <Button data-testid={`take-wave-${w.id}`} size="sm" className={`w-full rounded-full text-xs h-8 ${profileComplete ? 'bg-mango hover:bg-mango/90' : 'bg-neutral-400 hover:bg-neutral-500'}`}>{profileComplete ? <>{t("ui_ur_start")} <ArrowRight size={13} className="ml-1.5 rtl-flip-arrow" /></> : <>{t("ui_ur_complete_profile_arrow")}</>}</Button>
                </Link>
              </div>
            </div>
          ))}
          {ready && waves.length === 0 && (
            <div className="col-span-2 sm:col-span-3 bg-white border border-neutral-200 rounded-2xl p-8 shadow-sm" data-testid="panelist-empty-state">
              <div className="text-3xl">👋</div>
              <h3 className="text-2xl font-bold tracking-tight mt-3">{t("ui_ur_welcome_panel")}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</h3>
              <p className="text-neutral-600 mt-2 max-w-prose">
                {t("ui_ur_no_match_profile")}
              </p>
              <Link to="/panelist/profile" className="mt-5 rounded-xl border border-neutral-200 p-4 hover:bg-neutral-50 block max-w-xl" data-testid="empty-cta-profile">
                <div className="font-bold">{t("ui_ur_sharpen_profile")}</div>
                <div className="text-sm text-neutral-600 mt-1">
                  {t("ui_ur_sharpen_profile_body")}
                </div>
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {completed.map((c, i) => {
            // Read the wave's CURRENT points config — not the historical
            // ledger value — so if admin edits points mid-flight (or
            // post-flight) the patron sees the updated figure on their
            // completed list. We deliberately do NOT hardcode a fallback
            // number here: if the backend somehow returns a wave without
            // points_award, we render "—" instead of inventing "50".
            // Hardcoded business-value defaults silently lie to the user.
            const fullPts = Number.isFinite(c.wave?.points_award) ? c.wave.points_award : null;
            const screenPts = Number.isFinite(c.wave?.points_screened_out) ? c.wave.points_screened_out : null;
            // ── Tri-state classification: voided > screened > completed ──
            // Voided supersedes everything — an admin invalidated the
            // response (fast-fill / straight-line / fraud) and the points
            // were reversed. The patron sees a red badge + the reason.
            const isVoid = !!c.is_void;
            const isScreen = !isVoid && !!c.screened_out;
            const earned = isVoid ? 0 : (isScreen ? screenPts : fullPts);
            const earnedLabel = earned == null ? "—" : `${earned} ${t("ui_ur_points")}`;
            const screenLabel = screenPts == null ? "—" : `${screenPts} ${t("ui_ur_points")}`;
            // Human reason for void state — these strings come from the
            // backend's `void_reason` machine code; we map the common cases
            // to a translated ui_ur_void_reason_* label. Anything we don't
            // recognise falls through as the raw machine code (rare;
            // admin-set manual void with a custom string).
            const voidReason = (() => {
              const vr = (c.void_reason || "").toLowerCase();
              if (!vr) return t("ui_ur_void_reason_default");
              if (vr.startsWith("fast_fill")) return t("ui_ur_void_reason_fast_fill");
              if (vr.startsWith("straight_line")) return t("ui_ur_void_reason_straight_line");
              if (vr.includes("fraud")) return t("ui_ur_void_reason_fraud");
              if (vr.includes("manual")) return t("ui_ur_void_reason_manual");
              return vr.replace(/_/g, " ");
            })();
            const state = isVoid ? "void" : isScreen ? "screen" : "done";
            const statusLine =
              state === "void"
                ? t("ui_ur_status_voided").replace("{reason}", voidReason)
                : state === "screen"
                ? t("ui_ur_status_screened").replace("{points}", screenLabel)
                : t("ui_ur_status_completed").replace("{points}", earnedLabel);
            const badge =
              state === "void"
                ? { label: t("ui_ur_badge_voided"), cls: "border-red-200 text-red-700 bg-red-50" }
                : state === "screen"
                ? { label: t("ui_ur_badge_screened"), cls: "border-amber-200 text-amber-700 bg-amber-50" }
                : { label: t("ui_ur_badge_done"), cls: "border-emerald-200 text-emerald-700 bg-emerald-50" };
            return (
              <div key={i} className={`bg-white border border-neutral-200 rounded-2xl p-5 flex items-center justify-between gap-4 ${state === "void" ? "opacity-75" : ""}`} data-testid={`completed-${c.wave.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  {c.wave.cover_image && (
                    <div className="hidden sm:block w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-neutral-100" data-testid={`completed-image-${c.wave.id}`}>
                      <img src={c.wave.cover_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 dir={lang === "ur" ? "rtl" : "ltr"} className={`text-base font-bold ${state === "void" ? "line-through text-neutral-500" : ""}`}>
                      {/* Show wave name in Urdu when the patron picked اردو AND the wave
                          has an approved Urdu name AND the wave itself opted-in via
                          show_urdu_version. Fall back to English cell-by-cell. */}
                      {(lang === "ur"
                        && c.wave.show_urdu_version
                        && c.wave.name_ur_status === "approved"
                        && c.wave.name_ur) || c.wave.name}
                    </h3>
                    <div className="overline mt-1">{formatDate(c.completed_at)} · {statusLine}</div>
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full border font-bold tracking-wider shrink-0 ${badge.cls}`}>{badge.label}</span>
              </div>
            );
          })}
          {ready && completed.length === 0 && <div className="text-neutral-500 p-6 rounded-2xl border border-dashed border-neutral-200">{t("ui_ur_no_completed_yet")}</div>}
        </div>
      )}
    </div>
  );
}

// ITER 97 — dedicated full-page "Waves" view (sidebar nav item), for
// patrons who want to browse every wave without scrolling the dashboard.
// Same data sources + tab UI as PanelistHome's inline panel, just full-width.
export function PanelistWaves() {
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const [tab, setTab] = useState("available");
  const wavesSWR = useSWR(`/waves?lang=${lang}`, []);
  const waves = wavesSWR.data;
  const completedSWR = useSWR("/me/completed-waves", []);
  const completed = completedSWR.data;
  const completedNet = (completed || []).filter((c) => !c.is_void);
  const ready = wavesSWR.loaded && completedSWR.loaded;
  const prof = user?.profile || {};
  const profileComplete = !!prof.profile_complete && (prof.sectors_of_interest || []).length >= 3;

  return (
    <div className="max-w-4xl" data-testid="panelist-waves-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1">{t("ui_ur_nav_waves")}</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">All waves</h1>
        <p className="text-sm text-neutral-500 mt-1">Every wave you're eligible for, and everything you've already completed.</p>
      </div>
      <FadeIn ready={ready}>
        <WavesTabsPanel tab={tab} setTab={setTab} waves={waves} completed={completed} completedNet={completedNet} profileComplete={profileComplete} lang={lang} t={t} user={user} ready={ready} />
      </FadeIn>
    </div>
  );
}


function Card({ icon, label, value }) {
  return (
    <div className="bg-white border-2 border-black p-6 hard-shadow">
      <div className="flex items-center gap-3 mb-3">{icon}<div className="overline">{label}</div></div>
      <div className="font-mono text-3xl font-bold">{value}</div>
    </div>
  );
}

// ITER 69 — right-rail "latest content" card. Renders nothing when `item`
// is null (i.e. that feed has no published data yet) so the dashboard rail
// never shows an empty/dead card.
function LatestPanel({ label, item, viewAllHref, testid, t, highlightTitle }) {
  if (!item) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`${testid}-panel`}>
      <div className="flex items-center justify-between p-5 pb-3 gap-2">
        <div className="inline-block bg-mango/10 text-mango text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{label}</div>
        <Link to={viewAllHref} className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 hover:text-mango shrink-0" data-testid={`${testid}-view-all`}>{t("ui_ur_view_all_arrow")}</Link>
      </div>
      <Link to={item.href} className="block group" data-testid={`${testid}-link`}>
        {/* ITER 93 — blog-style cover image, same source as the WhatsApp
            share image (article cover_image / snapshot's first chart
            render) — a "latest X" card shouldn't look bare next to the
            actual blog/report pages, which always show one. */}
        {item.image && (
          <div className={`w-full aspect-[16/9] overflow-hidden ${item.isChart ? "bg-white" : "bg-neutral-100"}`}>
            <img
              src={item.image}
              alt=""
              className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${item.isChart ? "object-contain" : "object-cover"}`}
              data-testid={`${testid}-image`}
              loading="lazy"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
        )}
        <div className={`p-5 pt-4 transition-colors ${highlightTitle ? "bg-mango/25 group-hover:bg-mango/35" : ""}`}>
          {/* ITER 127 — snapshot/tracker cards get a full-width highlighted
              footer band (title + meta together), matching the reference
              design, rather than a small inline chip behind just the text. */}
          <div className="font-bold text-base leading-snug group-hover:text-mango transition-colors">{item.title}</div>
          {item.meta && <div className="text-xs text-neutral-500 mt-2 font-mono">{item.meta}</div>}
        </div>
      </Link>
    </div>
  );
}

/**
 * ✦ Founding Patron badge — displayed on the patron dashboard + profile page.
 * Awarded only to patrons whose row in `waitlist_signups` converted within the
 * admin-configurable `founding_patron_cap` (default 500). Direct signups via
 * /register never receive this — see backend `routers/auth.py` register handler.
 *
 * Visual: glossy gold-on-black pill that earns a hover-tooltip explaining the
 * lifetime perks (point multiplier + annual loyalty bonus).
 */
function FoundingPatronBadge({ t }) {
  return (
    <div className="group relative" data-testid="founding-patron-badge">
      <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3.5 py-1.5 font-semibold text-xs cursor-help">
        <span className="text-sm">✦</span>
        <span className="tracking-wide">{t("ui_ur_founding_patron")}</span>
      </div>
      <div className="absolute right-0 top-full mt-2 z-20 hidden group-hover:block w-72 bg-white border border-neutral-200 rounded-2xl shadow-lg p-4 text-xs text-left text-neutral-700">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2">Your lifetime perks</div>
        <ul className="space-y-1.5">
          <li><strong>✦ Permanent badge</strong> — visible across your account, forever.</li>
          <li><strong>+20% point multiplier</strong> on every survey you complete.</li>
          <li><strong>Annual loyalty bonus</strong> — 500 points credited each year on your join anniversary.</li>
          <li><strong>Exclusive invites</strong> — to events &amp; round-tables organised by oodel. and its partners.</li>
          <li><strong>Priority status</strong> in admin reports and respondent panels.</li>
        </ul>
      </div>
    </div>
  );
}

export { FoundingPatronBadge };

/**
 * 🎓 Campus Ambassador badge — appears the moment a patron is promoted to
 * Campus Ambassador (i.e. `user.is_campus_ambassador === true`). Visually
 * mirrors the Founding Patron pill but uses the mango palette so the two
 * are instantly distinguishable when a patron holds both.
 */
function CampusAmbassadorBadge({ t, onGreen }) {
  return (
    <div className="group relative" data-testid="campus-ambassador-badge">
      <div className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-semibold text-xs cursor-help ${onGreen ? "bg-white text-mango border border-white/60" : "bg-mango/10 text-mango border border-mango/30"}`}>
        <span className="text-sm">🎓</span>
        <span className="tracking-wide">{t("ui_ur_campus_ambassador")}</span>
      </div>
      <div className="absolute right-0 top-full mt-2 z-20 hidden group-hover:block w-72 bg-white border border-neutral-200 rounded-2xl shadow-lg p-4 text-xs text-left text-neutral-700">
        <div className="text-[11px] font-bold uppercase tracking-wider text-mango mb-2">You&apos;re a Campus Ambassador</div>
        <ul className="space-y-1.5">
          <li><strong>🎓 Cash per confirmed signup</strong> — every person you invite who completes their profile and takes a survey earns you a payout.</li>
          <li><strong>Surveys still work</strong> — you keep earning normal points by taking surveys yourself.</li>
          <li><strong>Your own dashboard</strong> — track invites, signups, and confirmed conversions on the <em>Ambassador</em> tab.</li>
        </ul>
      </div>
    </div>
  );
}

export { CampusAmbassadorBadge };

// Fixed core demographics — NOT admin-editable (regulator/methodology fixed).
// The rest of the lifestyle/wealth fields are pulled live from the Admin Profile DNA CMS:
//   GET /api/admin/profile-dna/public   →   [{key,label,section,kind,options,required,order,is_lifestyle}]
const REGIONS = ["Punjab", "Sindh", "KPK", "Balochistan", "Islamabad Capital Territory", "GB", "AJK"];
const COUNTRIES = ["UK", "USA", "Canada", "UAE", "Saudi Arabia", "Australia", "Germany", "Other"];

export function PanelistProfile() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLanguage();
  const nav = useNavigate();
  // When a patron lands here via the incomplete-profile redirect from
  // /panelist (or directly from a survey email), show a soft banner so they
  // know exactly what to do.
  const [searchParams] = useSearchParams();
  const fromIncomplete = searchParams.get("incomplete") === "1";
  const existing = user.profile || {};
  const ls = existing.lifestyle || {};
  const [allowOverseas, setAllowOverseas] = useState(false);
  const [sectors, setSectors] = useState([]);
  // Seed from the SWR cache so the gender / region / SEC pills paint in the
  // admin-configured order on first frame — without this seed the form
  // briefly rendered the hardcoded fallback order (Male, Female, Other) and
  // then flipped to the admin's custom order on the second tick.
  const [dnaFields, setDnaFields] = useState(() => api.getCached("/admin/profile-dna/public") || []);
  // Mirror of `cmsReady` for the profile-DNA endpoint. When the admin has
  // renamed a core label (e.g. "City" → "Country"), we must NOT paint the
  // hardcoded fallback ("City") for one frame before the API returns and
  // replaces it. Hold the Identity-section labels behind this flag so a
  // brand-new login (no cache) shows a tiny skeleton instead of flashing
  // the old default.
  const [dnaReady, setDnaReady] = useState(() => !!api.getCached("/admin/profile-dna/public"));
  const [phoneDialog, setPhoneDialog] = useState(false);
  const [siteContent, setSiteContent] = useState(getCachedSiteContent);
  // True once /site-content has come back at least once. CMS-driven labels
  // are held in a FadeIn until this flips so the user never sees the hard-
  // coded fallback text flash before the admin's customised value lands.
  const [cmsReady, setCmsReady] = useState(() => Object.keys(getCachedSiteContent()).length > 0);
  // Phone OTP toggle — when off (admin's site_content), we hide all phone
  // verification chrome (✓ chip on title, VERIFIED/UNVERIFIED pill) because
  // verification isn't actually happening — showing "unverified" while no
  // OTP is being sent would be a confusing dead-end for the user.
  const phoneOtpEnabled = String(siteContent?.phone_otp_enabled || "").toLowerCase() === "true";
  useEffect(() => {
    // /site-content is fetched ONCE per cold load by <CmsBoot> in App.js and
    // mirrored into the shared cache. Re-fetching it here on every profile
    // mount caused the visible "old label briefly → new label" flicker the
    // product owner reported when navigating back to this page. We now read
    // straight from the warmed cache (seeded above via getCachedSiteContent)
    // and trust CmsBoot to keep it fresh on every refresh.
    setAllowOverseas(String(siteContent.allow_overseas_panelist || "").toLowerCase() === "true");
    api.get("/sectors").then((r) => setSectors(r.data || [])).catch(() => {});
    // Dynamic DNA — admin can add/edit/reorder lifestyle fields from /admin/cms (Profile DNA tab).
    api.get("/admin/profile-dna/public")
      .then((r) => { setDnaFields(r.data || []); })
      .catch(() => {})
      .finally(() => { setDnaReady(true); });
  }, []);
  const [form, setForm] = useState({
    dob: existing.dob || "",
    gender: existing.gender || "",
    region: existing.region || "",
    city: existing.city || "",
    is_pakistan_resident: user.is_pakistan_resident !== false,
    country_of_residence: user.country_of_residence || "",
    sectors_of_interest: existing.sectors_of_interest || [],
    lifestyle: {
      // Seed with everything we have from the server. Dynamic DNA fields write into this map
      // by key — we don't need to pre-declare each one. Multi-select fields default to [].
      ...ls,
      // Preserve common ones that may be rendered conditionally even before DNA loads:
      dependents_count: ls.dependents_count || "",
      profession: ls.profession || "",
      interests: ls.interests || [],
    },
  });

  const updL = (k, v) => setForm({ ...form, lifestyle: { ...form.lifestyle, [k]: v } });
  const toggleArr = (k, v) => {
    const cur = form.lifestyle[k] || [];
    updL(k, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  const toggleSector = (key) => {
    const cur = form.sectors_of_interest || [];
    setForm({ ...form, sectors_of_interest: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validate CNIC (if a field of kind=cnic exists and is required).
    const cnicField = dnaFields.find((f) => f.kind === "cnic" && !f.hidden);
    if (cnicField && cnicField.required) {
      const cnicRaw = (form.lifestyle[cnicField.key] || "").replace(/\D/g, "");
      if (cnicRaw.length !== 13) {
        haptic.warn();
        toast.error(`Please enter your CNIC as xxxxx-xxxxxxx-x (13 digits).`);
        return;
      }
    }
    try {
      // Always send the full payload — including residency fields. The admin
      // `allow_overseas_panelist` toggle only controls whether the country
      // picker is RENDERED on the form; it must never strip data on submit,
      // because legacy overseas patrons may have country_of_residence
      // already set on their record. Stripping it caused the backend to
      // default to "Pakistan" and demand a Region they couldn't fill in.
      const payload = { ...form };
      const { data } = await api.put("/auth/profile", payload);
      await refresh();
      // Backend tells us whether this was the first-time completion and how
      // many welcome-bonus points it awarded (admin-configurable via
      // site_content.welcome_bonus_points). First save → celebrate +
      // bounce to dashboard. Subsequent edits → silent save, stay on page.
      if (data?.first_completion) {
        const bonus = Number(data.welcome_bonus || 0);
        if (bonus > 0) { haptic.success(); toast.success(`🎉 Profile complete — +${bonus} welcome bonus points!`); }
        else { haptic.medium(); toast.success("✓ Profile complete — you can now take surveys."); }
        // PHASE 8.35 — if this profile completion was triggered by an
        // incomplete-cohort or waitlist email, the URL carries an `after=`
        // query param pointing to the survey-take page. Honour it so the
        // patron is dropped straight into the survey they were invited to.
        const after = searchParams.get("after");
        nav(after || "/panelist", { replace: true });
      } else {
        haptic.tap();
        toast.success("✓ Profile updated.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    }
  };

  const Pill = ({ active, ...p }) => (
    <button type="button" {...p} className={`px-4 py-2 rounded-full border text-sm transition-colors ${active ? 'bg-mango text-white border-mango' : 'border-neutral-300 hover:border-mango text-neutral-700'} ${p.className || ''}`} />
  );
  const Sel = (props) => <select {...props} className="border border-neutral-300 rounded-xl px-3 py-2 bg-white text-sm focus:outline-none focus:border-mango" />;

  // Conditional hide: `fuel` only matters if a vehicle is owned.
  const shouldHide = (f) => {
    if (f.key === "fuel") {
      const v = form.lifestyle.vehicle;
      return !v || v === "None" || v === "No";
    }
    return false;
  };

  // Build a quick lookup of core-field admin overrides (label/required/hidden/options)
  // so the hardcoded Identity section can respect admin CMS edits without
  // duplicating the section itself. Core rows have `is_core: true` in
  // profile_dna_fields and are seeded with stable keys (country_of_residence,
  // city, region, gender, age_band, sec).
  const coreFieldMap = (() => {
    const m = {};
    for (const f of dnaFields) {
      if (f && f.is_core) m[f.key] = f;
    }
    return m;
  })();
  const coreOverride = (key, fallback) => {
    const o = coreFieldMap[key];
    if (!o) return fallback;
    return { ...fallback, ...o };
  };
  const isCoreHidden = (key) => !!(coreFieldMap[key] && coreFieldMap[key].hidden);
  const isCoreRequired = (key, fb = true) => {
    const o = coreFieldMap[key];
    return o ? !!o.required : fb;
  };
  const coreLabel = (key, fb) => (coreFieldMap[key]?.label || fb);
  const coreOptions = (key, fb) => {
    const o = coreFieldMap[key];
    return (o && Array.isArray(o.options) && o.options.length) ? o.options : fb;
  };
  const coreTooltip = (key) => (coreFieldMap[key]?.tooltip || "");

  // Group DNA fields by section, preserving the order admin set.
  // Returns: [[sectionName, fieldsArray], ...] in the order each section first
  // appears. Fields marked `hidden` (admin-disabled in CMS) or hidden by the
  // local conditional logic are filtered out FIRST so that a section whose
  // every field is hidden disappears entirely instead of rendering an empty
  // heading like "02 · IDENTITY" with no inputs underneath.
  // CRITICAL: rows flagged `is_core: true` are NEVER rendered in the dynamic
  // loop — they're already shown by the hardcoded "01 · IDENTITY" section
  // above. Likewise, any row whose section is "Identity" / "Identity (core)"
  // is pulled OUT of the dynamic loop and rendered INLINE inside the
  // hardcoded Identity block (e.g. CNIC), so we never get two Identity
  // sections on the form.
  const isIdentitySectionName = (sec) => {
    const s = (sec || "").toLowerCase().trim();
    return s === "identity" || s === "identity (core)";
  };
  const identityInlineFields = dnaFields.filter(
    (f) => f && !f.is_core && !f.hidden && !shouldHide(f) && isIdentitySectionName(f.section)
  );
  const dnaSections = (() => {
    const order = [];
    const map = new Map();
    for (const f of dnaFields) {
      if (f.is_core) continue;
      if (f.hidden) continue;
      if (shouldHide(f)) continue;
      if (isIdentitySectionName(f.section)) continue;  // rendered inline above
      const sec = f.section || "Custom";
      if (!map.has(sec)) { map.set(sec, []); order.push(sec); }
      map.get(sec).push(f);
    }
    return order.map((s) => [s, map.get(s)]);
  })();

  // Render a single DNA field by its `kind`. Writes into form.lifestyle[key].
  const renderDnaField = (f) => {
    if (shouldHide(f)) return null;
    if (f.hidden) return null;                          // admin hard-hide
    const val = form.lifestyle[f.key];
    const opts = f.options || [];
    const tt = f.tooltip || "";                          // editable via /admin/profile-dna
    if (f.kind === "cnic") {
      // CNIC = Pakistani national-ID. Format xxxxx-xxxxxxx-x (13 digits + 2 dashes).
      // We auto-mask: user types digits only, dashes appear automatically.
      const formatCNIC = (raw) => {
        const d = (raw || "").replace(/\D/g, "").slice(0, 13);
        if (d.length <= 5) return d;
        if (d.length <= 12) return `${d.slice(0, 5)}-${d.slice(5)}`;
        return `${d.slice(0, 5)}-${d.slice(5, 12)}-${d.slice(12)}`;
      };
      const cnicLabel = (siteContent.cnic_label || "CNIC / NICOP").trim() || "CNIC / NICOP";
      const cnicDisclaimer = (siteContent.cnic_disclaimer || "Please enter your CNIC (or NICOP if you are an overseas Pakistani) correctly. You will need it to claim your prize if you win the raffle or redeem any vouchers.").trim();
      return (
        <Field key={f.key} label={cnicLabel} required={f.required} tooltip={tt}>
          <Input
            type="text"
            inputMode="numeric"
            data-testid={`dna-${f.key}`}
            value={formatCNIC(val)}
            onChange={(e) => updL(f.key, formatCNIC(e.target.value))}
            placeholder="xxxxx-xxxxxxx-x"
            maxLength={15}
            className="rounded-xl w-full md:w-72 font-mono tracking-wide"
          />
          {cnicDisclaimer && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 mt-2 max-w-2xl border border-amber-200" data-testid="cnic-disclaimer">
              ⚠ {cnicDisclaimer}
            </p>
          )}
        </Field>
      );
    }
    if (f.kind === "pill_single") {
      // Guard rail: if a patron's already-saved value isn't in the CURRENT
      // admin-configured options (e.g. an admin renamed "Undergrad" ->
      // "Bachelor" after this patron already picked "Undergrad"), still
      // render it as its own selected pill instead of letting it silently
      // vanish — a required field with a real saved value must never look
      // unselected. The patron can still switch to a current option anytime.
      const renderOpts = (val && !opts.includes(val)) ? [...opts, val] : opts;
      return (
        <Field key={f.key} label={f.label} required={f.required} tooltip={tt}>
          <div className="flex flex-wrap gap-2">
            {renderOpts.map((o) => (
              <Pill key={o} data-testid={`dna-${f.key}-${o}`} active={val === o} onClick={() => updL(f.key, o)}>{o}</Pill>
            ))}
          </div>
        </Field>
      );
    }
    if (f.kind === "pill_multi") {
      const arr = Array.isArray(val) ? val : [];
      // Same guard rail as pill_single — keep any legacy selected values
      // visible even if an admin has since removed/renamed that option.
      const legacy = arr.filter((v) => !opts.includes(v));
      const renderOpts = legacy.length ? [...opts, ...legacy] : opts;
      return (
        <Field key={f.key} label={f.label} required={f.required} tooltip={tt}>
          <div className="flex flex-wrap gap-2">
            {renderOpts.map((o) => (
              <Pill key={o} data-testid={`dna-${f.key}-${o}`} active={arr.includes(o)} onClick={() => toggleArr(f.key, o)}>{o}</Pill>
            ))}
          </div>
        </Field>
      );
    }
    if (f.kind === "dropdown") {
      // Same guard rail — a legacy value not in the current list gets
      // appended as its own <option> so the <select> doesn't silently fall
      // back to blank/first-item while a real value is actually saved.
      const dropdownOpts = (val && !opts.includes(val)) ? [...opts, val] : opts;
      return (
        <Field key={f.key} label={f.label} required={f.required} tooltip={tt}>
          <Sel data-testid={`dna-${f.key}`} value={val || ""} onChange={(e) => updL(f.key, e.target.value)}>
            <option value="">Select {f.label.toLowerCase()}</option>
            {dropdownOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </Sel>
        </Field>
      );
    }
    if (f.kind === "number") {
      return (
        <Field key={f.key} label={f.label} required={f.required} tooltip={tt}>
          <Input type="number" data-testid={`dna-${f.key}`} value={val || ""} onChange={(e) => updL(f.key, e.target.value)} className="rounded-xl w-32" />
        </Field>
      );
    }
    // default: text
    return (
      <Field key={f.key} label={f.label} required={f.required} tooltip={tt}>
        <Input type="text" data-testid={`dna-${f.key}`} value={val || ""} onChange={(e) => updL(f.key, e.target.value)} className="rounded-xl w-full md:w-96" />
      </Field>
    );
  };

  // Where should the referral card live? Profile-page placement is admin-
  // configurable via site_content.referral_card_visibility ("home" | "profile" | "hidden").
  const referralVisibility = String(siteContent?.referral_card_visibility || "").trim().toLowerCase();
  return (
    <div className="max-w-3xl">
      {referralVisibility === "profile" && !user.is_growth_lead && (
        <div className="mb-6"><ReferralCard /></div>
      )}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1">PROFILE</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900 inline-flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>Your panelist profile</span>
            {phoneOtpEnabled && user.phone_verified && (
              <span title="Phone verified" data-testid="profile-verified-badge" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-mango text-white text-base font-bold">✓</span>
            )}
          </h1>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          {user.is_campus_ambassador && <CampusAmbassadorBadge t={t} />}
          {user.is_founding_patron && <FoundingPatronBadge t={t} />}
        </div>
      </div>
      <FadeIn ready={cmsReady}>
      <p className="text-neutral-500 mb-3" data-testid="profile-heading">{siteContent.panelist_profile_heading || "Answer accurately. We use this to match you with relevant surveys — your classification is hidden and never shared."}</p>
      </FadeIn>

      {fromIncomplete && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-4 max-w-3xl shadow-sm" data-testid="profile-incomplete-banner">
          <div className="font-bold text-amber-900">Finish your profile to take this survey</div>
          <p className="text-xs text-amber-800 mt-1">A new survey is waiting for you. Complete the fields below and you&apos;ll be matched automatically.</p>
        </div>
      )}

      {/* 🔒 Privacy First disclaimer — CMS-editable so legal/admin can
          rephrase without a deploy. Renders an always-on default if both
          CMS keys are missing/undefined, so the block is visible to
          impersonated sessions, fresh logins, AND first paint before the
          site_content fetch lands. Block hides only when BOTH p1 and p2
          have been explicitly set to "" by admin. */}
      <FadeIn ready={cmsReady}>
      {(() => {
        const sc = siteContent || {};
        const p1d = "To make sure survey results reflect Pakistan accurately, we collect broad demographic information (such as age, region, education, and income bracket) to improve research quality. We never ask for passwords, account numbers/balances or other information that could be used to access your accounts or identify your survey responses.";
        const p2d = "All responses are confidential and analysed only in aggregate. We ask for ranges and categories, not exact amounts or account details. Our goal is to understand communities and trends.";
        const p1 = sc.panelist_profile_privacy_p1 === undefined ? p1d : sc.panelist_profile_privacy_p1;
        const p2 = sc.panelist_profile_privacy_p2 === undefined ? p2d : sc.panelist_profile_privacy_p2;
        const p3 = (sc.panelist_profile_privacy_p3 || "").trim();
        if (!p1 && !p2 && !p3) return null;
        return (
          <div className="bg-mango/5 border border-mango/20 rounded-2xl p-4 mb-6 text-[11px] leading-relaxed text-neutral-700 max-w-3xl" data-testid="profile-privacy">
            <div className="font-bold text-neutral-900 mb-1">🔒 Privacy First</div>
            {p1 && <p data-testid="profile-privacy-p1">{p1}</p>}
            {p2 && <p className="mt-2" data-testid="profile-privacy-p2">{p2}</p>}
            {p3 && <p className="mt-2" data-testid="profile-privacy-p3">{p3}</p>}
          </div>
        );
      })()}
      </FadeIn>

      {/* Phone-required gate banner — appears when admin has set
          site_content.phone_required_to_save_profile=true AND the user
          hasn't yet added a phone number. The backend enforces this on
          save too, but surfacing it up here means the user knows BEFORE
          they fill the whole form. */}
      {(() => {
        const phoneReq = String(siteContent?.phone_required_to_save_profile || "").trim().toLowerCase() === "true";
        if (!phoneReq) return null;
        const hasPhone = !!user.phone_number;
        const verified = !!user.phone_verified;
        if (hasPhone && (verified || !phoneOtpEnabled)) return null;
        return (
          <div className="bg-red-50 border border-red-300 rounded-2xl p-4 mb-4 max-w-3xl shadow-sm" data-testid="phone-required-banner">
            <div className="font-bold text-red-900">{!hasPhone ? "Phone number required" : "Verify your phone to continue"}</div>
            <p className="text-xs text-red-800 mt-1">
              {!hasPhone
                ? "Admin has made phone number a required field. Please add a phone below before saving your profile."
                : "Admin has enabled OTP verification. Click ‘Change phone’ above and complete the OTP step to finish."}
            </p>
          </div>
        );
      })()}

      {/* Name (display name) — editable inline. Self-service edit with
          server-side fake-name validation. Lives directly above the phone
          panel so the user always sees the most important identifying
          field first. */}
      <NameEditor user={user} refresh={refresh} />

      {/* Phone & verification panel */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-8 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="profile-phone-panel">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-neutral-400">PHONE NUMBER</div>
          <div className="text-sm mt-1.5">
            {user.phone_number || <span className="text-neutral-400 italic">Not set yet</span>}
            {phoneOtpEnabled && user.phone_number && (user.phone_verified
              ? <span className="ml-2 text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">VERIFIED ✓</span>
              : <span className="ml-2 text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold">UNVERIFIED</span>)}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="rounded-full"
          onClick={() => setPhoneDialog(true)}
          data-testid="change-phone-btn"
        >
          {user.phone_number ? "Change phone" : "Add phone"}
        </Button>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {!dnaReady ? (
          <Section title="01 · IDENTITY">
            <div className="text-sm text-neutral-400 italic" data-testid="identity-loading">Loading…</div>
          </Section>
        ) : (
        <Section title="01 · IDENTITY">
          {allowOverseas && !isCoreHidden("country_of_residence") && (
            <Field label={coreLabel("country_of_residence", "Country of residence")} tooltip={coreTooltip("country_of_residence")}>
              <p className="text-xs text-neutral-500 mb-2">Pick your country first — the city dropdown will adapt automatically. Choose <b>Other</b> in the city list if your town isn't listed.</p>
            </Field>
          )}
          {!(isCoreHidden("country_of_residence") && isCoreHidden("city")) && (
            <Field label={allowOverseas ? coreLabel("city", "Country & City") : coreLabel("city", "City")} tooltip={coreTooltip("city")}>
              <CountryCitySelect
                country={form.country_of_residence || "Pakistan"}
                city={form.city}
                onChange={({ country, city }) => setForm({
                  ...form,
                  country_of_residence: country,
                  is_pakistan_resident: country === "Pakistan",
                  city,
                  // Reset region when leaving Pakistan — overseas users don't have PK provinces.
                  region: country === "Pakistan" ? form.region : "",
                })}
                className="w-full md:w-[640px]"
              />
            </Field>
          )}
          {!isCoreHidden("dob") && (
            <Field label={coreLabel("dob", "Date of birth")} tooltip={coreTooltip("dob") || coreTooltip("age_band")}><DobPicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} required={isCoreRequired("dob", true)} testIdPrefix="dob" /></Field>
          )}
          {!isCoreHidden("gender") && (
            <Field label={coreLabel("gender", "Gender")} required={isCoreRequired("gender", true)} tooltip={coreTooltip("gender")}><div className="flex gap-2">{coreOptions("gender", ["Male", "Female", "Other"]).map((g) => <Pill key={g} data-testid={`gender-${g}`} active={form.gender === g} onClick={() => setForm({ ...form, gender: g })}>{g}</Pill>)}</div></Field>
          )}
          {/* Region picker — always visible. Overseas patrons can still pick
              the region they identify with (e.g. an overseas Pakistani from
              Punjab). Backend treats region as optional when country != PK
              but accepts whatever the patron supplies. */}
          {!isCoreHidden("region") && (
            <Field label={form.country_of_residence && form.country_of_residence !== "Pakistan" ? coreLabel("region", "Region you identify with") : coreLabel("region", "Region")} required={isCoreRequired("region", true)} tooltip={coreTooltip("region")}><div className="flex flex-wrap gap-2">{coreOptions("region", REGIONS).map((r) => <Pill key={r} data-testid={`region-${r}`} active={form.region === r} onClick={() => setForm({ ...form, region: r })}>{r}</Pill>)}</div></Field>
          )}
          <Field label="Dependents (children + others)"><Input type="number" min="0" max="20" data-testid="dependents" value={form.lifestyle.dependents_count} onChange={(e) => updL("dependents_count", e.target.value)} className="rounded-xl w-24" /></Field>
          <Field label="Profession / job title" required={false}><Input data-testid="profession" value={form.lifestyle.profession} onChange={(e) => updL("profession", e.target.value)} className="rounded-xl w-full md:w-96" placeholder="e.g., Marketing Manager (optional)" /></Field>
          {/* Identity-section DNA rows (e.g. CNIC) are rendered INLINE here
              instead of as a separate "02 · IDENTITY" section, so the form
              never shows two identity blocks. Admin can still hide/edit
              them via the Profile DNA CMS. */}
          {identityInlineFields.map(renderDnaField)}
        </Section>
        )}

        {/* DYNAMIC DNA SECTIONS — admin-editable from /admin/cms (Profile DNA tab). */}
        {dnaSections.length === 0 && (
          <div className="bg-white border-2 border-dashed border-neutral-300 p-6 text-sm text-neutral-500" data-testid="dna-loading">
            Loading profile questions…
          </div>
        )}
        {dnaSections.map(([sectionName, fields], idx) => (
          <Section key={sectionName} title={`${String(idx + 2).padStart(2, '0')} · ${sectionName.toUpperCase()}`}>
            {fields.map(renderDnaField)}
          </Section>
        ))}

        <Section title={`${String(dnaSections.length + 2).padStart(2, '0')} · AREAS OF INTEREST — pick at least 3`}>
          <p className="text-xs text-neutral-500 mb-3">We'll match you with surveys in these sectors. <strong>Minimum 3 required.</strong> Currently picked: <span className={`font-bold ${(form.sectors_of_interest || []).length >= 3 ? 'text-green-700' : 'text-red-600'}`}>{(form.sectors_of_interest || []).length}</span></p>
          <div className="flex flex-wrap gap-2" data-testid="sectors-picker">
            {sectors.map((s) => (
              <Pill
                key={s.key}
                data-testid={`sector-${s.key}`}
                active={(form.sectors_of_interest || []).includes(s.key)}
                onClick={() => toggleSector(s.key)}
              >{s.icon} {lang === "ur" && (s.label_ur || "").trim() ? s.label_ur : s.label}</Pill>
            ))}
          </div>
        </Section>

        <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
          <Button data-testid="save-profile" type="submit" className="bg-mango hover:bg-mango/90 text-white rounded-full px-6">Save profile</Button>
          <p className="text-xs text-neutral-500 mt-3">Every field is required to keep oodel data statistically valid. You cannot take surveys until your profile is complete.</p>
        </div>
      </form>
      {phoneDialog && <ChangePhoneDialog onClose={() => setPhoneDialog(false)} onSaved={() => { refresh(); setPhoneDialog(false); }} />}
    </div>
  );
}

// Two-step phone change dialog.
// Step 1: re-enter current password + new phone (E.164). Backend verifies the
//         password and dispatches an OTP via the configured provider (mock or Twilio).
// Step 2: enter the 6-digit code → backend swaps phone_number and marks
//         phone_verified=true.
// If admin has disabled OTP, step 1 returns `skipped_otp:true` and we just close.
function ChangePhoneDialog({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [pw, setPw] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [mockCode, setMockCode] = useState(null);  // dev-only hint
  const [pendingPhone, setPendingPhone] = useState("");

  const submitStep1 = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/me/change-phone/request", { password: pw, phone });
      if (r.data.skipped_otp) {
        toast.success("Phone updated.");
        onSaved();
        return;
      }
      setPendingPhone(r.data.new_phone);
      if (r.data.dev_mock_code) setMockCode(r.data.dev_mock_code);
      toast.success(`Code sent via ${r.data.channel}. Check your messages.`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send code");
    } finally { setBusy(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/me/change-phone/verify", { code });
      toast.success("Phone verified ✓");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code didn't match");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="change-phone-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="bg-neutral-900 text-white p-5 flex items-center justify-between">
          <div>
            <div className="text-mango text-[10px] font-bold uppercase tracking-wider">{step === 1 ? "STEP 1 / 2" : "STEP 2 / 2"}</div>
            <h3 className="text-xl font-bold tracking-tight mt-1">{step === 1 ? "Change your phone" : "Enter the code we sent"}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none">×</button>
        </div>
        {step === 1 ? (
          <form onSubmit={submitStep1} className="p-5 space-y-4">
            <p className="text-sm text-neutral-600">For your security, please re-enter your password and the new phone number with country code (e.g. <span className="font-mono">+92 300 1234567</span>).</p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Current password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="phone-pw" autoComplete="current-password" className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">New phone (with country code)</Label>
              <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+92 300 1234567" data-testid="phone-new" className="rounded-xl mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-mango text-white hover:bg-mango/90" data-testid="phone-send-otp">{busy ? "Sending…" : "Send code"}</Button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitStep2} className="p-5 space-y-4">
            <p className="text-sm text-neutral-600">We sent a 6-digit code to <span className="font-mono">{pendingPhone}</span>. Enter it below to confirm.</p>
            {mockCode && (
              <div className="bg-amber-50 rounded-xl border border-amber-200 px-3 py-2 text-xs">
                <strong>DEV mode:</strong> Twilio not configured — use code <span className="font-mono font-bold">{mockCode}</span>.
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">6-digit code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} required maxLength={6} inputMode="numeric" data-testid="phone-code" className="font-mono text-lg tracking-widest rounded-xl mt-1" />
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setStep(1)} className="rounded-full">← Back</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-mango text-white hover:bg-mango/90" data-testid="phone-verify">{busy ? "Verifying…" : "Verify"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5 shadow-sm">
      <div className="text-xs font-bold uppercase tracking-wider text-mango">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children, required = true, tooltip = "" }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
        <span>{label}</span>
        {required && <span className="text-red-500" title="Required field">*</span>}
        {tooltip && (
          <span
            className="relative inline-flex items-center group"
            data-testid="dna-tooltip-icon"
          >
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-neutral-100 text-neutral-500 text-[10px] font-bold cursor-help select-none hover:bg-neutral-200"
              aria-label={`Help: ${tooltip}`}
            >?</span>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 z-50 hidden group-hover:block group-focus-within:block bg-neutral-900 text-white text-xs leading-snug px-3 py-2 rounded-xl shadow-lg w-64 normal-case tracking-normal"
            >{tooltip}</span>
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}

export function SurveyTake() {
  const { id } = useNavigateParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { lang, t } = useLanguage();
  const [wave, setWave] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  // Pre-survey intro screen — shown once before question 1.
  // Skipped on retry-after-fast-fill and after the user clicks "Continue".
  const [introShown, setIntroShown] = useState(false);
  // Scroll the user back to the top every time the current question changes
  // (Next / Back). Without this, a user who scrolled down to answer the
  // previous question lands halfway through the next question and has to
  // scroll up to read it. Also fires when the intro dismisses and Q1 mounts.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch { window.scrollTo(0, 0); }
  }, [step, introShown]);
  const [siteContent, setSiteContent] = useState(getCachedSiteContent);
  // Detect "already submitted" upfront so the user sees a clear status
  // screen instead of being dropped into question-1 and only learning the
  // wave is closed when they hit Submit at the end.
  const [alreadySubmitted, setAlreadySubmitted] = useState(() => {
    const cached = api.getCached("/me/completed-waves") || [];
    return cached.some((row) => (row.wave?.id || row.id || row.wave_id) === id);
  });
  // Post-submit thanks screen — shows the points earned & a Close button that
  // returns the user to their dashboard.
  const [thanksData, setThanksData] = useState(null);
  useEffect(() => {
    api.get("/site-content").then((r) => setSiteContent(r.data || {})).catch(() => {});
    api.get("/me/completed-waves").then((r) => {
      const done = (r.data || []).some((row) => (row.wave?.id || row.id || row.wave_id) === id);
      setAlreadySubmitted(done);
    }).catch(() => {});
  }, [id]);

  // iter27 — guard direct URL navigation. Bounce to /profile if not complete.
  useEffect(() => {
    const prof = user?.profile || {};
    const ok = !!prof.profile_complete && (prof.sectors_of_interest || []).length >= 3;
    if (!ok) {
      toast.error("Complete your profile (incl. 3+ areas of interest) first.");
      nav("/panelist/profile", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    api.get(`/waves/${id}?lang=${lang}`).then((r) => setWave(r.data));
    // Speed-Trap: server-stamped start_time
    api.post(`/waves/${id}/start`).then((r) => setStartedAt(r.data.started_at)).catch(() => {});
  }, [id, lang]);

  if (!wave) return <div className="p-12">Loading…</div>;

  // Already-submitted state — clean, dignified status screen instead of
  // a confusing "wave already submitted" error toast deep into the flow.
  if (alreadySubmitted) {
    const isUr = lang === "ur";
    const recordedWithName = t("ui_ur_already_recorded").replace("{name}", "___NAME___");
    const [recBefore, recAfter] = recordedWithName.split("___NAME___");
    return (
      <div className="max-w-2xl p-4 md:p-8" data-testid="survey-already-submitted">
        <div className="bg-white border border-neutral-200 rounded-3xl shadow-sm p-8" dir={isUr ? "rtl" : "ltr"}>
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">{t("ui_ur_already_pill")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900 mb-3">{t("ui_ur_already_title")}</h1>
          <p className="text-neutral-600 mb-2">
            {recBefore}<span className="font-bold">{wave.title || wave.name}</span>{recAfter}
          </p>
          <p className="text-sm text-neutral-500 mb-6">
            {t("ui_ur_already_integrity")}
          </p>
          <div className="flex gap-3">
            <Button onClick={() => nav("/panelist")} className="rounded-full bg-mango hover:bg-mango/90 text-white" data-testid="back-to-dashboard">{t("ui_ur_back_to_dashboard")}</Button>
            <Button onClick={() => nav("/panelist?tab=completed")} variant="outline" className="rounded-full" data-testid="view-completed">{t("ui_ur_view_completed")}</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- PRE-SURVEY INTRO SCREEN ----------------
  // Resolution rule (operator-driven):
  //   • If admin EXPLICITLY cleared BOTH `intro_topic` AND `intro_text` on the
  //     wave, that's a signal: "don't show an intro for this wave". Skip
  //     directly to Q1 — do not fall back to the global default.
  //   • If admin set EITHER field, render the intro (filling missing pieces
  //     with the global defaults / placeholders).
  const hasWaveIntro = Boolean((wave.intro_topic && wave.intro_topic.trim()) || (wave.intro_text && wave.intro_text.trim()));
  const rawTemplate = hasWaveIntro ? (wave.intro_text || siteContent.survey_intro_default || "").trim() : "";
  const showIntro = rawTemplate.length > 0;
  if (showIntro && !introShown) {
    const topic = wave.intro_topic || siteContent.survey_intro_default_topic || "this research";
    const points = wave.points_award != null ? wave.points_award : (siteContent.default_points_award || 50);
    const introText = rawTemplate
      .replace(/\{\{\s*topic\s*\}\}/gi, String(topic))
      .replace(/\{\{\s*points\s*\}\}/gi, String(points));
    const isUr = lang === "ur";
    return (
      <div className="max-w-2xl p-4 md:p-0" data-testid="survey-intro">
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1.5">WAVE · {wave.name}</div>
        <div className="text-sm mb-4 text-neutral-400" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_intro_expected").replace("{minutes}", String(wave.expected_minutes || 3))}</div>
        <div className="bg-white border border-neutral-200 rounded-3xl shadow-sm p-8">
          <h2 className="text-2xl font-bold tracking-tight mb-6 text-neutral-900" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_before_you_start")}</h2>
          <div className="space-y-4 text-base leading-relaxed text-neutral-700" dir={isUr ? "rtl" : "ltr"}>
            {introText.split(/\n{2,}/).map((para, i) => (
              <p key={i} data-testid={`intro-para-${i}`} className="whitespace-pre-line">{para}</p>
            ))}
          </div>
          <div className="mt-8 flex justify-between items-center" dir={isUr ? "rtl" : "ltr"}>
            <button onClick={() => nav("/panelist")} className="text-sm text-neutral-400 hover:text-neutral-700" data-testid="intro-cancel">{isUr ? "→" : "←"} {t("ui_ur_back")}</button>
            <Button
              data-testid="intro-continue"
              onClick={() => {
                setIntroShown(true);
                // Reset the speed-trap clock so the intro reading time isn't counted
                // against the per-question minimum.
                setStartedAt(new Date().toISOString());
              }}
              className="bg-mango hover:bg-mango/90 text-white rounded-full px-8"
            >
              {t("ui_ur_continue")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ───── Thanks screen ─────
  // Shown after a successful submission. The user can click "Close" to go
  // back to the dashboard. We intentionally do not auto-redirect — the user
  // should see and acknowledge the points they just earned.
  if (thanksData) {
    const isUr = lang === "ur";
    const responseRecorded = t("ui_ur_response_recorded").replace("{name}", "___NAME___");
    const [respBefore, respAfter] = responseRecorded.split("___NAME___");
    return (
      <div className="max-w-xl mx-auto py-8 px-4" data-testid="survey-thanks-screen">
        <div className="bg-white border border-neutral-200 rounded-3xl shadow-sm p-8 text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-mango mb-4" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_survey_submitted")}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 mb-6" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_thanks_title")}</h1>
          <p className="text-base text-neutral-600 mb-8" dir={isUr ? "rtl" : "ltr"}>
            {respBefore}<span className="font-bold">{thanksData.waveName}</span>{respAfter}
          </p>
          <div className="rounded-2xl bg-mango/5 border border-mango/20 px-6 py-5 mb-8 inline-block">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_you_earned")}</div>
            <div className="text-5xl font-bold tracking-tight text-mango tabular-nums" data-testid="thanks-points">
              +{thanksData.points}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-1" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_points_label")}</div>
          </div>
          <div>
            <Button
              onClick={() => { setThanksData(null); nav("/panelist"); }}
              className="bg-mango hover:bg-mango/90 text-white rounded-full px-10 py-3 text-sm font-bold uppercase tracking-wider"
              data-testid="thanks-close-btn"
            >
              {t("ui_ur_close_arrow")}
            </Button>
          </div>
          <p className="text-[10px] text-neutral-400 mt-4 italic" dir={isUr ? "rtl" : "ltr"}>{t("ui_ur_redirect_notice")}</p>
        </div>
      </div>
    );
  }

  const q = wave.questions[step];
  const total = wave.questions.length;

  const setAnswer = (qid, val) => setAnswers({ ...answers, [qid]: val });

  const toggleMulti = (qid, key, exclusive) => {
    const current = answers[qid] || [];
    if (exclusive) { setAnswer(qid, current.includes(key) ? [] : [key]); return; }
    const exclusiveKeys = q.options.filter((o) => o.exclusive).map((o) => o.key);
    let next = current.filter((k) => !exclusiveKeys.includes(k));
    if (next.includes(key)) next = next.filter((k) => k !== key);
    else next = [...next, key];
    if (q.max_selections && next.length > q.max_selections) {
      toast.error(`Max ${q.max_selections} selections`); return;
    }
    setAnswer(qid, next);
  };

  const next = () => {
    if (!answers[q.id] || answers[q.id].length === 0) { toast.error("Please answer the question"); return; }
    if (step < total - 1) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/waves/${id}/take`, { answers, started_at: startedAt });
      // FAST-FILL — first offense: response REJECTED, patron may retry (we keep them on the runner).
      if (data.rejected && data.reason === "fast_fill_first_offense") {
        toast.error(
          `⚠ ${data.message}`,
          { duration: 9000 }
        );
        // Reset the start clock so the retry has a fresh window.
        setStartedAt(new Date().toISOString());
        setStep(0);
        setSubmitting(false);
        return;
      }
      // FAST-FILL — second offense: wave closed for this patron.
      if (data.closed_for_user) {
        toast.error(`🚫 ${data.message}`, { duration: 10000 });
        await refresh();
        nav("/panelist");
        return;
      }
      await refresh();
      if (data.is_void) { haptic.warn(); toast.error(data.message, { duration: 8000 }); nav("/panelist"); }
      else if (data.screened_out) { haptic.tap(); toast(data.message, { duration: 6000 }); nav("/panelist"); }
      else {
        haptic.success();
        trackEvent("survey_complete", {
          wave_id: id,
          wave_name: wave.name,
          sector_key: wave.sector_key || "general",
          points_awarded: data.points_awarded || 0,
        });
        // Show dedicated thanks screen with points earned — user clicks Close
        // to return to dashboard. (Replaces the previous toast + auto-redirect.)
        setThanksData({ points: data.points_awarded || 0, waveName: wave.name });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const isUr = lang === "ur";
  const optDir = isUr ? "rtl" : "ltr";
  // Selected-option styling uses mango (brand) — high-contrast in both
  // light and dark mode, and matches the sitewide accent colour.
  const selCls  = "border-mango bg-mango text-white";
  const idleCls = "border-neutral-200 hover:border-mango/50";

  return (
    <div className="max-w-2xl p-4 md:p-0">
      <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1.5">WAVE · {wave.name}</div>
      <div className="text-sm text-neutral-400 mb-4">Q{step + 1} / {total} · expected ~{wave.expected_minutes || 3} min</div>
      <div className="h-1.5 bg-neutral-100 rounded-full mb-8 overflow-hidden">
        <div className="h-full bg-mango rounded-full transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
      </div>
      <div className="bg-white border border-neutral-200 rounded-3xl shadow-sm p-6 md:p-8">
        <h2 className="text-2xl font-bold tracking-tight mb-6 text-neutral-900" data-testid="survey-question" dir={optDir}>{q.text}</h2>
        {q.format === "single" && (
          <div className="space-y-2.5">
            {q.options.map((o) => (
              <button key={o.key} data-testid={`opt-${o.key}`} onClick={() => setAnswer(q.id, [o.key])} dir={optDir} className={`w-full text-start border rounded-2xl p-4 transition-colors ${answers[q.id]?.[0] === o.key ? selCls : idleCls}`}>
                {o.text}
              </button>
            ))}
          </div>
        )}
        {q.format === "multiple" && (
          <div className="space-y-2.5">
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3">{isUr ? t("ui_ur_select_all") : `SELECT UP TO ${q.max_selections || q.options.length}`}</div>
            {q.options.map((o) => {
              const sel = (answers[q.id] || []).includes(o.key);
              return (
                <button key={o.key} data-testid={`opt-${o.key}`} onClick={() => toggleMulti(q.id, o.key, o.exclusive)} dir={optDir} className={`w-full text-start border rounded-2xl p-4 flex items-center gap-3 transition-colors ${sel ? selCls : idleCls}`}>
                  <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${sel ? 'bg-white border-white' : 'border-neutral-300'}`}>{sel && <span className="text-mango font-bold text-xs">✓</span>}</span>
                  {o.text}
                </button>
              );
            })}
          </div>
        )}
        {(q.format === "nps" || q.format === "likert") && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3">{q.format === "nps" ? "0 = Not at all likely · 10 = Extremely likely" : "1 = Strongly disagree · 5 = Strongly agree"}</div>
            <div className={`grid ${q.format === "nps" ? "grid-cols-11" : "grid-cols-5"} gap-1.5`}>
              {Array.from({ length: q.format === "nps" ? 11 : 5 }, (_, i) => {
                const val = String(i + (q.format === 'likert' ? 1 : 0));
                return (
                  <button key={i} onClick={() => setAnswer(q.id, [val])} data-testid={`scale-${val}`} className={`h-12 rounded-xl border transition-colors ${answers[q.id]?.[0] === val ? selCls : idleCls}`}>
                    {val}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex justify-between mt-8 pt-6 border-t border-neutral-100" dir={optDir}>
          {step === 0 ? <span />: (
            <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full" data-testid="survey-back" dir={optDir}>{isUr ? "→" : "←"} {t("ui_ur_back")}</Button>
          )}
          <Button data-testid="survey-next" disabled={submitting} onClick={next} className="bg-mango hover:bg-mango/90 text-white rounded-full px-6" dir={optDir}>
            {step === total - 1 ? (submitting ? "Submitting..." : t("ui_ur_submit")) : t("ui_ur_next")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Helper to use useParams without separate import block confusion
import { useParams } from "react-router-dom";
function useNavigateParams() { return useParams(); }

export function PanelistRewards() {
  const { user, refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [redeemed, setRedeemed] = useState(null);
  const [confirmReward, setConfirmReward] = useState(null); // reward pending redeem confirmation
  const [redeeming, setRedeeming] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null); // reward image URL shown full-size, or null
  const [tab, setTab] = useState("vouchers"); // 'vouchers' | 'cash' | 'charity'
  const isResident = user?.is_pakistan_resident !== false;

  const load = async () => {
    const [r, h] = await Promise.all([api.get("/rewards"), api.get("/me/redemptions")]);
    setRewards(r.data);
    setRedemptions(h.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!isResident) setTab("charity"); }, [isResident]);

  const redeem = async (rid) => {
    setRedeeming(true);
    try {
      const { data } = await api.post(`/rewards/${rid}/redeem`);
      setRedeemed(data);
      setConfirmReward(null);
      await refresh();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Redemption failed");
    } finally {
      setRedeeming(false);
    }
  };

  // Filter rewards by tab + residency
  const filtered = rewards.filter((r) => {
    const dt = r.delivery_type || "unique_code";
    if (!isResident && dt !== "charity") return false;
    if (tab === "charity") return dt === "charity";
    if (tab === "cash") return dt === "manual_cashout";
    if (tab === "vouchers") return ["unique_code", "global_code", "qr_image"].includes(dt);
    return true;
  });

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-1">REWARDS STORE</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">Spend your points</h1>
        {!isResident && (
          <div className="bg-mango/5 border border-mango/20 rounded-2xl p-4 mt-3 text-sm text-neutral-700" data-testid="overseas-banner">
            As an overseas Pakistani, you can convert your points into charitable donations supporting Pakistan.
          </div>
        )}
      </div>

      {redeemed && <RedemptionReceiptDialog redeemed={redeemed} onClose={() => setRedeemed(null)} />}
      {lightboxImage && <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />}
      {confirmReward && (
        <RedeemConfirmDialog
          reward={confirmReward}
          busy={redeeming}
          onCancel={() => setConfirmReward(null)}
          onConfirm={() => redeem(confirmReward.id)}
        />
      )}

      {isResident && (
        <div className="flex items-center gap-1 border-b border-neutral-200">
          <button onClick={() => setTab("vouchers")} data-testid="tab-vouchers" className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === 'vouchers' ? 'border-mango text-mango' : 'border-transparent text-neutral-500 hover:text-black'}`}>Vouchers</button>
          <button onClick={() => setTab("cash")} data-testid="tab-cash" className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === 'cash' ? 'border-mango text-mango' : 'border-transparent text-neutral-500 hover:text-black'}`}>Cash-Out</button>
          <button onClick={() => setTab("charity")} data-testid="tab-charity" className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === 'charity' ? 'border-mango text-mango' : 'border-transparent text-neutral-500 hover:text-black'}`}>Charity</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {filtered.map((r) => {
          const cantAfford = (user.display_points ?? user.points) < r.points_cost;
          const belowMin = (r.min_points_to_redeem || 0) > 0 && (user.display_points ?? user.points) < r.min_points_to_redeem;
          const noStock = r.stock_info && !r.stock_info.unlimited && r.stock_info.remaining <= 0;
          return (
            <div key={r.id} className="bg-white border border-neutral-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col" data-testid={`reward-${r.id}`}>
              {r.image && (
                <img
                  src={r.image}
                  alt={r.name}
                  className="w-full aspect-[2/1] object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                  data-testid={`reward-image-${r.id}`}
                  onClick={() => setLightboxImage(r.image)}
                />
              )}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-mango bg-mango/10 rounded-full px-2 py-0.5">{r.delivery_type === 'charity' ? 'CHARITY' : r.delivery_type === 'manual_cashout' ? 'CASH-OUT' : 'VOUCHER'}</span>
                  {r.charity_name && <span className="text-xs font-bold text-mango">{r.charity_name}</span>}
                  {r.partner_name && (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500" data-testid={`reward-partner-${r.id}`}>
                      {r.partner_logo && <img src={r.partner_logo} alt={r.partner_name} className="w-5 h-5 rounded-full object-contain border border-neutral-200 bg-white" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                      {r.partner_name}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-neutral-900">{r.name}</h3>
                <p className="text-sm text-neutral-500 mt-1 flex-1">{r.description}</p>
                {(r.min_points_to_redeem || 0) > 0 && <div className="text-xs text-neutral-400 mt-1">Min {r.min_points_to_redeem} pts</div>}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-100">
                  <div className="text-xl font-bold text-neutral-900">{r.points_cost} pts</div>
                  <Button data-testid={`redeem-${r.id}`} disabled={cantAfford || belowMin || noStock} onClick={() => setConfirmReward(r)} className="bg-mango hover:bg-mango/90 text-white rounded-full">
                    {noStock ? "Out of stock" : belowMin ? "Below min" : cantAfford ? "Need more pts" : (r.delivery_type === "charity" ? "Donate" : "Redeem")}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="md:col-span-3 text-neutral-500 p-8 rounded-2xl border border-dashed border-neutral-200">No rewards in this category yet.</div>}
      </div>

      {redemptions.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3">YOUR REDEMPTION HISTORY</div>
          <div className="space-y-2">
            {redemptions.map((r) => (
              <button key={r.id} onClick={() => setRedeemed(r)} data-testid={`redemption-history-${r.id}`} className="w-full text-left bg-white border border-neutral-200 hover:border-mango/40 rounded-xl p-4 flex justify-between items-center text-sm transition-colors">
                <div className="flex items-center gap-2">
                  {r.partner_logo && <img src={r.partner_logo} alt="" className="w-6 h-6 rounded-full object-contain border border-neutral-200 bg-white shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                  <span>{r.reward_name} <span className="text-neutral-400 ml-2">{formatDate(r.redeemed_at)}</span> {r.fulfillment_status && <span className="ml-2 text-xs text-neutral-400 uppercase">· {r.fulfillment_status}</span>}</span>
                </div>
                <div className="font-mono text-xs max-w-xs truncate text-neutral-500">{r.code}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Confirm-before-redeem dialog — prevents an accidental tap on "Redeem"
// from instantly spending points / burning a one-time code with no way
// back. Shown for every delivery type (voucher, cash-out, charity).
function RedeemConfirmDialog({ reward, busy, onCancel, onConfirm }) {
  const isCharity = reward.delivery_type === "charity";
  const isCashout = reward.delivery_type === "manual_cashout";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="redeem-confirm-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <h3 className="text-lg font-bold text-neutral-900">{isCharity ? "Confirm donation?" : "Confirm redemption?"}</h3>
        <p className="text-sm text-neutral-600 mt-2">
          {isCharity
            ? <>You're about to donate <span className="font-bold text-neutral-900">{reward.points_cost} points</span> to <span className="font-bold text-neutral-900">{reward.charity_name || reward.name}</span>. This cannot be undone.</>
            : isCashout
              ? <>You're about to spend <span className="font-bold text-neutral-900">{reward.points_cost} points</span> on <span className="font-bold text-neutral-900">{reward.name}</span>. Our team will contact you to process the cash-out. This cannot be undone.</>
              : <>You're about to spend <span className="font-bold text-neutral-900">{reward.points_cost} points</span> to redeem <span className="font-bold text-neutral-900">{reward.name}</span>. Your voucher code will be issued immediately and this cannot be undone.</>}
        </p>
        <div className="flex gap-3 mt-5">
          <Button variant="outline" className="flex-1 rounded-full" disabled={busy} onClick={onCancel} data-testid="redeem-confirm-cancel">Cancel</Button>
          <Button className="flex-1 bg-mango hover:bg-mango/90 text-white rounded-full" disabled={busy} onClick={onConfirm} data-testid="redeem-confirm-proceed">
            {busy ? "Processing…" : isCharity ? "Confirm donation" : "Confirm redemption"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Full-size image viewer — clicking a reward's thumbnail opens the same
// image large/uncropped (the card thumbnail is a 2:1 cropped preview).
function ImageLightbox({ src, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="image-lightbox"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-5 left-5 text-white/70 hover:text-white text-sm" data-testid="image-lightbox-close">Close ✕</button>
      <img src={src} alt="" className="max-w-full max-h-[85vh] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}



// Redemption proof-of-redemption receipt — a popup dialog (not inline),
// opened right after redeeming AND reopenable anytime from "Your
// redemption history". Shows the partner's logo when the reward is
// linked to one, so a patron can screenshot it as in-store proof.
function RedemptionReceiptDialog({ redeemed, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="redeem-receipt-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-neutral-900 text-white rounded-2xl shadow-xl max-w-md w-full p-6 relative" data-testid="redeem-success">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 hover:text-white text-xs" data-testid="redeem-receipt-close">Close ✕</button>
        <div className="text-xs font-bold uppercase tracking-wider text-mango mb-2">{redeemed.delivery_type === "charity" ? "DONATION LOGGED" : redeemed.delivery_type === "manual_cashout" ? "CASH-OUT REQUESTED" : "VOUCHER UNLOCKED"}</div>
        <div className="flex items-center gap-3">
          {redeemed.partner_logo && (
            <img src={redeemed.partner_logo} alt={redeemed.partner_name || ""} className="w-12 h-12 rounded-xl object-contain border border-white/10 bg-white shrink-0" data-testid="redeem-receipt-partner-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div>
            <h3 className="text-xl font-bold leading-tight">{redeemed.reward_name}</h3>
            {redeemed.partner_name && <div className="text-xs text-neutral-400 mt-0.5">from {redeemed.partner_name}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <div className="font-mono text-sm bg-white text-neutral-900 rounded-xl px-4 py-2 max-w-full break-words" data-testid="redeem-receipt-code">{redeemed.code}</div>
          {redeemed.code && (
            <button
              onClick={() => { navigator.clipboard.writeText(redeemed.code); toast.success("Code copied"); }}
              className="text-xs font-bold text-mango hover:text-white border border-mango/40 rounded-full px-3 py-2 shrink-0"
              data-testid="redeem-receipt-copy"
            >Copy</button>
          )}
        </div>
        {redeemed.charity_name && <div className="mt-3 text-sm text-neutral-300">Donating to <span className="text-white font-bold">{redeemed.charity_name}</span> — thank you!</div>}
        <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-400">
          <div>Receipt ref: <span className="text-neutral-200 font-mono" data-testid="redeem-receipt-ref">{redeemed.redemption_ref || "—"}</span></div>
          <div>Redeemed: <span className="text-neutral-200">{redeemed.redeemed_at ? formatDateTime(redeemed.redeemed_at) : "—"}</span></div>
          <div>Points spent: <span className="text-neutral-200">{redeemed.points_spent ?? "—"}</span></div>
        </div>
        <p className="mt-3 text-xs text-neutral-400">Keep this receipt as proof of redemption — screenshot it or reopen it anytime from "Your redemption history".</p>
      </div>
    </div>
  );
}

// ============================================================================
// PanelistPoints — full point ledger for the logged-in patron.
// Two tabs:
//   - "Earned" → every credit (surveys, bonuses, screen-outs) reverse-chrono
//   - "Raffle" → only raffle deductions (the prize-claim events)
// Summary header shows lifetime earned, lifetime spent, # surveys, # wins.
// ============================================================================
export function PanelistPoints() {
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("earned");
  const nav = useNavigate();
  useEffect(() => {
    api.get("/auth/me/points-history")
      .then((r) => setHistory(r.data))
      .catch(() => setHistory({ rows: [], by_kind: {}, totals: {}, config: { enabled: true, tabs: { earned: true, raffle: true, redemptions: true }, labels: {} } }));
  }, []);
  // If admin has disabled the whole feature, bounce the patron home.
  useEffect(() => {
    if (history && history.config && history.config.enabled === false) {
      nav("/panelist", { replace: true });
    }
  }, [history, nav]);
  if (!history) return <div className="p-6">Loading…</div>;
  const t = history.totals || {};
  const cfg = history.config || { tabs: {}, labels: {} };
  const labels = cfg.labels || {};
  // Build the visible tab list from the admin-controlled flags. "earned" is
  // always on — it's the core of the feature. The other two are toggleable.
  const tabDefs = [
    ["earned", labels.tab_earned || "Earned", true],
    ["raffle", labels.tab_raffle || "Raffle wins", cfg.tabs?.raffle !== false],
    ["redemptions", labels.tab_redemptions || "Redemptions", cfg.tabs?.redemptions !== false],
  ].filter(([, , visible]) => visible);
  // If the currently-selected tab was just hidden, fall back to the first visible one.
  const activeTab = tabDefs.find(([k]) => k === tab) ? tab : tabDefs[0]?.[0] || "earned";
  const rows = (history.by_kind || {})[activeTab] || [];
  const fmtDate = (iso) => formatDate(iso);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="panelist-points-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-mango">YOUR POINTS</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900 mt-1">{labels.title || "Points history"}</h1>
        <p className="text-sm text-neutral-500 mt-1">{labels.subtitle || "Every credit and debit on your account — explained."}</p>
      </div>

      {/* Headline tiles — three only. current = earned − spent (true by ledger math). */}
      <div className="grid grid-cols-3 gap-3">
        <Tile label={labels.tile_current || "CURRENT BALANCE"} value={(history.current_balance ?? 0).toLocaleString()} highlight />
        <Tile label={labels.tile_earned  || "LIFETIME EARNED"} value={(t.lifetime_earned ?? 0).toLocaleString()} />
        <Tile label={labels.tile_spent   || "LIFETIME SPENT"}  value={(t.lifetime_spent  ?? 0).toLocaleString()} />
      </div>

      {/* Tab bar — only renders tabs the admin has enabled. Tab counters
          subtract voided/reversal rows so the patron sees their NET earnings
          count, not the raw row count (which would double-count void+reversal). */}
      <div className="flex items-center gap-1 border-b border-neutral-200" data-testid="points-tabs">
        {tabDefs.map(([k, lab]) => {
          const all = history.by_kind?.[k] || [];
          const net = all.filter((r) => !r.voided && !r.is_reversal);
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`points-tab-${k}`}
              className={`px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${activeTab === k ? "border-mango text-mango" : "border-transparent text-neutral-500 hover:text-black"}`}
            >
              {lab} ({net.length})
            </button>
          );
        })}
      </div>

      {/* Ledger table (desktop/tablet) + stacked cards (mobile) — same row
          data + classification logic, two renderings. The old markup was a
          single fixed 5-column <table> inside an `overflow-hidden` wrapper,
          which silently CLIPPED the POINTS/BALANCE columns off-screen on
          narrow phones instead of scrolling or reflowing. */}
      <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden" data-testid="points-ledger">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">
            {activeTab === "earned" && "No earnings yet — take your first survey to start banking points."}
            {activeTab === "raffle" && "No raffle prizes yet. Keep earning to enter draws."}
            {activeTab === "redemptions" && "No rewards redeemed yet."}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-400">DATE</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-400">EVENT</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-400">DETAILS</th>
                    <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-400">POINTS</th>
                    <th className="text-right px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-400">BALANCE</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const c = classifyLedgerRow(r);
                    return (
                      <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`ledger-row-${r.id}`}>
                        <td className={`px-4 py-3 text-xs text-neutral-400 whitespace-nowrap ${c.dim}`}>{fmtDate(r.created_at)}</td>
                        <td className={`px-4 py-3 font-medium ${c.dim}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{r.reason_label}</span>
                            {c.badge && (
                              <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-full border ${c.badge.cls}`}>{c.badge.lab}</span>
                            )}
                          </div>
                          {c.subNote && <div className="text-[10px] text-red-500 mt-0.5 italic">{c.subNote}</div>}
                        </td>
                        <td className={`px-4 py-3 text-neutral-500 text-xs ${c.dim}`}>{r.note || "—"}</td>
                        <td className={`px-4 py-3 text-right font-bold ${c.isVoid ? "line-through text-neutral-400" : r.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {r.delta > 0 ? "+" : ""}{r.delta.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-right text-neutral-600 ${c.dim}`}>{(r.balance_after ?? 0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile: stacked cards — every column still visible, just
                reflowed vertically instead of clipped. */}
            <div className="md:hidden divide-y divide-neutral-100">
              {rows.map((r) => {
                const c = classifyLedgerRow(r);
                return (
                  <div key={r.id} className="p-4" data-testid={`ledger-row-mobile-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className={`font-medium text-sm ${c.dim}`}>{r.reason_label}</div>
                      <div className={`font-bold text-sm shrink-0 ${c.isVoid ? "line-through text-neutral-400" : r.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {r.delta > 0 ? "+" : ""}{r.delta.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className={`text-xs text-neutral-400 ${c.dim}`}>{fmtDate(r.created_at)}</span>
                      {c.badge && (
                        <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-full border ${c.badge.cls}`}>{c.badge.lab}</span>
                      )}
                    </div>
                    {r.note && <div className={`text-xs text-neutral-500 mt-1 ${c.dim}`}>{r.note}</div>}
                    {c.subNote && <div className="text-[10px] text-red-500 mt-0.5 italic">{c.subNote}</div>}
                    <div className="text-[11px] text-neutral-400 mt-1.5">Balance after: <span className="text-neutral-600 font-medium">{(r.balance_after ?? 0).toLocaleString()}</span></div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Shared row-classification for the points ledger — used by both the
// desktop table and the mobile card list above so the void/reversal/
// screen-out visual rules never drift between the two layouts.
function classifyLedgerRow(r) {
  const isVoid = !!r.voided;
  const isReversal = !!r.is_reversal || (r.reason || "").endsWith("_reversal") || (r.reason === "fraud_penalty" && r.delta < 0);
  const isScreen = r.reason === "survey_screened_out";
  const dim = isVoid ? "line-through text-neutral-400" : "";
  const reasonText = (r.void_reason || r.note || "").toLowerCase();
  const voidReasonHuman = reasonText.startsWith("fast_fill") ? "completed too quickly"
    : reasonText.startsWith("straight_line") ? "same answer to every question"
    : reasonText.includes("manual") ? "manually invalidated"
    : reasonText.includes("fraud") ? "quality / fraud flag"
    : reasonText.replace(/_/g, " ");
  const subNote = isVoid && voidReasonHuman ? `Voided · reason: ${voidReasonHuman}` : null;
  let badge = null;
  if (isVoid) badge = { lab: "VOIDED", cls: "bg-red-50 text-red-700 border-red-200" };
  else if (isReversal) badge = { lab: "REVERSAL", cls: "bg-red-50 text-red-600 border-red-200" };
  else if (isScreen) badge = { lab: "SCREENED OUT", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { isVoid, isReversal, isScreen, dim, subNote, badge };
}

function Tile({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 shadow-sm ${highlight ? "bg-mango text-white" : "bg-white border border-neutral-200"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl md:text-3xl font-bold tracking-tight mt-1">{value}</div>
    </div>
  );
}
